/**
 * portfolio.ts — investor portfolio dashboard + property detail + deal analyzer (Phase 1C/1D).
 *
 * Screens:
 *   portfolio       → dashboard (hero, metric pills, quick actions, saved deals, property list)
 *   portfolioDetail → single-property drill-down with Overview/Financials/Transactions tabs
 *
 * Modals:
 *   Add Property    → existing
 *   Log Income      → existing (kept for backward compat)
 *   Log Expense     → existing (kept for backward compat)
 *   Log Transaction → unified income/expense (new — preferred quick-add)
 *   Deal Analyzer   → stateless calculator backed by POST /portfolio/deals/analyze
 *
 * Gated at the router level (Portfolio tab appears only when
 * state.user.user_type === 'investor'), but we also render an empty-state
 * fallback if the screen gets reached via direct navigation by a buyer.
 */

import { api } from './api.js';
import { trackEvent } from './analytics.js';
import { getProviderCtaUrl, SPONSORED_DISCLOSURE } from './providerCtas.js';
import {
  glossBreakeven, glossCapRate, glossCashInvested, glossCashOnCash, glossDepreciation, glossDscr,
  glossGrm, glossLoanBalance, glossLtv, glossNoi, glossTaxSavings, glossTotalRoi,
} from '../../shared/glosses.js';
import { pillHtml } from '../../shared/markup.js';
import { buildPortfolioPills } from '../../shared/portfolioPills.js';
import type { PortfolioPillSummary } from '../../shared/portfolioPills.js';
import { compactMagnitude } from '../../shared/format.js';
import { NA, toNumber } from '../../shared/tiles.js';
import { cashOnCashLevel } from '../../shared/underwriting.js';
import { navigate } from './router.js';
// state is read only for the investor's saved tax bracket (Schedule E
// savings estimate) — there are no user_type gates here.
import { state } from './state.js';
import { escapeHtml, showToast } from './ui.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

// ─── Compliance disclaimers (mirror of backend/app/constants/disclaimers.py) ──
// Centralizing here so the frontend can render the same text that the
// backend returns in responses. Legal updates a single source.
const DISCLAIMER = {
  reps: (
    'Hour tracking for documentation purposes only. Does not determine '
    + 'your qualification for Real Estate Professional Status under IRC '
    + '§469(c)(7). Consult a qualified CPA. The IRS has not endorsed '
    + 'any hour-tracking software.'
  ),
  exchange: (
    '1031 deadline tracking is informational only. Not legal or tax '
    + 'advice. Deadlines may shift with weekends, holidays, and IRS '
    + 'extensions. Confirm dates with your qualified intermediary.'
  ),
  capitalGains: (
    'Capital gains estimate uses top federal LTCG (20%), §1250 recapture '
    + '(25%), NIIT (3.8%), and a user-supplied state rate. Actual '
    + 'liability depends on your return. Not tax advice.'
  ),
  costSeg: (
    'Preliminary cost-seg screening estimate only. Commission a licensed '
    + 'engineer and consult your CPA before claiming accelerated '
    + 'depreciation. 100% bonus depreciation permanent under OBBBA 2025.'
  ),
  benchmark: (
    'Benchmark returns compare your estimated annualized portfolio return '
    + 'against historical S&P 500 and VGSLX total returns. Past performance '
    + 'does not guarantee future results. Not investment advice.'
  ),
  rentMarket: (
    'Market data is sourced exclusively from publicly available listings and '
    + 'government databases (HUD, Census). SwipeHome does not aggregate '
    + 'non-public rental data. Not a rent recommendation.'
  ),
};

function complianceFooter(text: string): string {
  return `<div class="compliance-footer">${escapeHtml(text)}</div>`;
}

function haptic(ms: number = 30): void {
  try { navigator.vibrate?.(ms); } catch { /* unsupported */ }
}

// Selection state for the detail screen lives here — no need to persist across
// sessions so we don't pollute the global state object.
let selectedPortfolioId: string | null = null;
let selectedPortfolio: AnyRecord | null = null;
let selectedDetailTab: 'overview' | 'financials' | 'transactions' | 'tax' | 'capex' | 'rehab' = 'overview';
let sortKey: 'cash_flow' | 'cap_rate' | 'equity' | 'recent' = 'recent';

// ═══════════════════════════════════════════════════════════════
// DASHBOARD SCREEN
// ═══════════════════════════════════════════════════════════════

export function renderPortfolio(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'fade-in screen-padded portfolio-screen';
  div.innerHTML = `
    <div class="portfolio-ai-bar" onclick="openPortfolioAiStub()">
      <i data-feather="message-square" width="14"></i>
      <input type="text" placeholder="Ask about your portfolio..." readonly>
      <i data-feather="arrow-right" width="14"></i>
    </div>

    <div id="portfolio-hero" class="portfolio-hero">
      <div class="portfolio-hero-skeleton skeleton"></div>
    </div>

    <div id="portfolio-pills" class="portfolio-pills-scroll"></div>

    <div class="portfolio-quick-actions">
      <button class="portfolio-qa-btn portfolio-qa-primary" onclick="openAddPortfolioModal()">
        <i data-feather="plus" width="15"></i>
        <span>Add property</span>
      </button>
      <button class="portfolio-qa-btn" onclick="openDealAnalyzer()">
        <i data-feather="activity" width="15"></i>
        <span>Analyze deal</span>
      </button>
      <button class="portfolio-qa-btn" onclick="navigate('portfolioAnalytics')">
        <i data-feather="bar-chart-2" width="15"></i>
        <span>Analytics</span>
      </button>
      <button class="portfolio-qa-btn" onclick="openPortfolioQuickTx()">
        <i data-feather="edit-3" width="15"></i>
        <span>Log txn</span>
      </button>
    </div>

    <div id="portfolio-active-flips"></div>

    <div class="portfolio-section-header">
      <h3 class="portfolio-section-head">REPS Hours ${new Date().getFullYear()}</h3>
      <button class="portfolio-link-btn" onclick="openRepsModal()">+ Log hours</button>
    </div>
    <div id="portfolio-reps">
      <div class="skeleton-row"><div class="skeleton" style="height:40px;border-radius:8px"></div></div>
    </div>

    <div class="portfolio-section-header">
      <h3 class="portfolio-section-head">1031 Exchanges</h3>
      <button class="portfolio-link-btn" onclick="openExchangeModal()">+ New</button>
    </div>
    <div id="portfolio-exchanges">
      <div class="skeleton-row"><div class="skeleton" style="height:40px;border-radius:8px"></div></div>
    </div>

    <!-- Redundancy #2 final: Pipeline lived here briefly but it covered the
         same data as Watchlist (right-swiped deals not yet owned). After
         Options 1 + 2 the Watchlist card already carries the status chip
         + the "..." action menu (Mark as Offer Sent / Under Contract /
         I Bought This / Lost). Pipeline was redundant. Portfolio is now
         a pure ownership surface: net worth, KPIs, sparkline, REPS hours,
         1031 exchanges, and the actual Owned Properties below. -->

    <div class="portfolio-section-header">
      <h3 class="portfolio-section-head">Owned Properties</h3>
      <div class="portfolio-sort">
        <select id="portfolio-sort-select" onchange="sortPortfolioBy(this.value)">
          <option value="recent">Recently added</option>
          <option value="cash_flow">Cash flow</option>
          <option value="cap_rate">Cap rate</option>
          <option value="equity">Equity</option>
        </select>
      </div>
    </div>
    <div id="portfolio-list">
      <div class="skeleton-row"><div class="skeleton" style="height:48px;border-radius:10px"></div></div>
    </div>
  `;
  setTimeout(() => {
    loadPortfolio();
    loadReps();
    loadExchanges();
    // loadSavedDeals removed — Pipeline section deleted (see comment in
    // the JSX above). The endpoint + helpers stay around for now so the
    // existing convert + delete flows still work when called from
    // Watchlist; a follow-up cleanup pass can prune what's truly unused.
    if (window.feather) window.feather.replace();
  }, 50);
  return div;
}

// ─── Loaders ────────────────────────────────────────────────────────────

/**
 * Dashed empty-state row shared by every Portfolio section (same look as
 * the 1031 Exchanges empty state). Used both when a fetch returns nothing
 * and when it fails: an API error body ("Not Found", a stack trace) is
 * never rendered — it goes to the console and the user sees a sentence
 * plus an optional retry.
 */
function sectionEmpty(icon: string, text: string, retry?: string): string {
  return `
    <div class="portfolio-saved-empty${retry ? ' portfolio-saved-empty-retry' : ''}"${retry ? ` onclick="${retry}" role="button" tabindex="0"` : ''}>
      <i data-feather="${icon}" width="18"></i>
      <span>${escapeHtml(text)}${retry ? ' <strong>Tap to retry.</strong>' : ''}</span>
    </div>
  `;
}

function sectionUnavailable(id: string, what: string, retry: string, err: unknown): void {
  console.warn(`[portfolio] ${what} failed to load`, err);
  const el = document.getElementById(id);
  if (!el) return;
  el.innerHTML = sectionEmpty('wifi-off', `Couldn't load ${what} right now.`, retry);
  if (window.feather) window.feather.replace();
}

export async function loadPortfolio(): Promise<void> {
  try {
    const [dashboard, list] = await Promise.all([
      api<AnyRecord>('GET', '/portfolio/dashboard'),
      api<AnyRecord>('GET', '/portfolio'),
    ]);
    renderHero(dashboard);
    renderPills(dashboard);
    renderList((list.items as AnyRecord[]) || []);
  } catch (err: any) {
    sectionUnavailable('portfolio-list', 'your properties', 'loadPortfolio()', err);
  }
}

// renderNonInvestorFallback deleted — investor-first convergence.
// Portfolio is available to all users. Empty state handled by renderList().

// ─── Hero (net worth + trend + equity bar) ──────────────────────────────

function renderHero(s: AnyRecord): void {
  const el = document.getElementById('portfolio-hero');
  if (!el) return;

  const value = Number(s.total_portfolio_value || 0);
  const debt = Number(s.total_debt || 0);
  // `??`, not `||`: a fully-leveraged portfolio with $0 equity must read
  // $0, not fall back to the portfolio value.
  const equity = Number(s.total_equity ?? value);
  const trend = Array.isArray(s.monthly_cash_flow_trend) ? s.monthly_cash_flow_trend : [];

  // Trend delta = last two COMPLETED months of actuals. The final point is
  // the current, partial month — comparing against it reads as a swing
  // that hasn't happened yet.
  let deltaLabel = '';
  let deltaDir = 'flat';
  const completed = trend.slice(0, -1);
  if (completed.length >= 2) {
    const last = Number(completed[completed.length - 1]?.amount ?? 0);
    const prev = Number(completed[completed.length - 2]?.amount ?? 0);
    const d = last - prev;
    if (Math.abs(d) >= 1) {
      deltaDir = d > 0 ? 'up' : 'down';
      deltaLabel = `${d > 0 ? '↑' : '↓'} ${fmtMoney(Math.abs(d))} vs last month`;
    }
  }

  // Equity bar — fill pct = equity / value.
  const equityPct = value > 0 ? Math.max(0, Math.min(100, (equity / value) * 100)) : 100;

  el.innerHTML = `
    <div class="portfolio-hero-inner">
      <div class="portfolio-hero-label">Portfolio net worth</div>
      <div class="portfolio-hero-value">${fmtMoneyCompact(equity)}</div>
      ${deltaLabel ? `<div class="portfolio-hero-delta portfolio-hero-delta-${deltaDir}">${escapeHtml(deltaLabel)}</div>` : ''}
      <div class="portfolio-hero-equity-bar" title="Equity vs debt">
        <div class="portfolio-hero-equity-fill" style="width:${equityPct.toFixed(1)}%"></div>
      </div>
      <div class="portfolio-hero-equity-meta">
        <span>${fmtMoneyCompact(equity)} equity</span>
        <span>${fmtMoneyCompact(debt)} debt</span>
      </div>
      ${renderActualCashFlow(trend, Number(s.actual_monthly_cash_flow_avg ?? 0))}
    </div>
  `;
}

/**
 * ACTUAL cash flow — the last 12 calendar months of logged transactions,
 * as opposed to the PROJECTED figure in the KPI strip (rent + expense
 * lanes). Rendered only when at least one month has a transaction: with
 * nothing logged the block is omitted entirely, so the hero card never
 * carries a label over an empty chart area. The sparkline is inline SVG
 * (same path builder as the mobile Sparkline) — no Chart.js dependency,
 * so a blocked CDN can't leave a blank canvas behind either.
 */
function renderActualCashFlow(trend: AnyRecord[], avg: number): string {
  const data = trend.map((t) => Number(t.amount || 0));
  if (!data.some((v) => Math.abs(v) > 0)) return '';
  const months = data.length;
  return `
    <div class="portfolio-actual-cf">
      <div class="portfolio-actual-cf-head">
        <span class="portfolio-actual-cf-label">Actual · last ${months} mo</span>
        <span class="portfolio-actual-cf-value">${fmtMoney(avg)}/mo avg</span>
      </div>
      ${sparklineSvg(data, 56)}
    </div>
  `;
}

/** Indigo line + translucent area; the y-range always includes 0. */
function sparklineSvg(data: number[], height: number): string {
  const n = data.length;
  if (n === 0) return '';
  const W = 320; // viewBox width; the SVG stretches to the card (preserveAspectRatio none)
  const PAD = 3;
  const min = Math.min(0, ...data);
  const max = Math.max(0, ...data);
  const span = max - min || 1;
  const pts = data.map((v, i) => ({
    x: n === 1 ? W / 2 : (i / (n - 1)) * W,
    y: PAD + (1 - (v - min) / span) * (height - PAD * 2),
  }));
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 1; i < n; i++) {
    const p0 = pts[i - 1];
    const p1 = pts[i];
    const dx = (p1.x - p0.x) * 0.35;
    d += ` C ${(p0.x + dx).toFixed(1)} ${p0.y.toFixed(1)}, ${(p1.x - dx).toFixed(1)} ${p1.y.toFixed(1)}, ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`;
  }
  const last = pts[n - 1];
  const area = `${d} L ${last.x.toFixed(1)} ${height} L ${pts[0].x.toFixed(1)} ${height} Z`;
  const zeroY = PAD + (1 - (0 - min) / span) * (height - PAD * 2);
  return `
    <svg class="portfolio-actual-cf-spark" viewBox="0 0 ${W} ${height}" height="${height}" preserveAspectRatio="none" aria-hidden="true">
      <path d="${area}" fill="rgba(99,102,241,0.18)"></path>
      <line x1="0" x2="${W}" y1="${zeroY.toFixed(1)}" y2="${zeroY.toFixed(1)}" stroke="rgba(255,255,255,0.12)" stroke-dasharray="3 3" vector-effect="non-scaling-stroke"></line>
      <path d="${d}" fill="none" stroke="rgba(99,102,241,0.95)" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke"></path>
    </svg>
  `;
}

function renderPills(s: AnyRecord): void {
  const el = document.getElementById('portfolio-pills');
  if (!el) return;
  // Shared builder (web + mobile): six pills, every one with an explicit
  // value — a metric the payload can't provide reads "n/a", never blank —
  // and each tile names itself "Label: value" for assistive tech
  // (shared/markup.ts). The strip is a wrapping grid, so all six are on
  // screen at phone width instead of two scrolled off the right edge.
  el.innerHTML = buildPortfolioPills(s as PortfolioPillSummary)
    .map((p) => pillHtml(p, escapeHtml))
    .join('');
}

// ─── Saved deals ────────────────────────────────────────────────────────

export async function loadSavedDeals(): Promise<void> {
  const el = document.getElementById('portfolio-saved-deals');
  if (!el) return;
  try {
    const data = await api<AnyRecord>('GET', '/portfolio/saved-deals');
    const items = (data.items as AnyRecord[]) || [];
    if (items.length === 0) {
      el.innerHTML = `
        <div class="portfolio-saved-empty">
          <i data-feather="bookmark" width="18"></i>
          <span>Right-swipe a deal on Discover to add it to your pipeline. The Watchlist tab shows them all on a map.</span>
        </div>
      `;
      if (window.feather) window.feather.replace();
      return;
    }
    el.innerHTML = items.map(renderSavedDealRow).join('');
    if (window.feather) window.feather.replace();
  } catch (err: any) {
    el.innerHTML = `<div style="color:var(--text-secondary);font-size:13px;padding:8px 0">${escapeHtml(err.message || 'Failed to load saved deals')}</div>`;
  }
}

function gradeColor(grade: string): string {
  if (grade === 'A') return 'var(--success)';
  if (grade === 'B') return 'var(--success)';
  if (grade === 'C') return 'var(--warning)';
  return 'var(--danger)';
}

function dealStatusLabel(s?: string): string {
  const map: Record<string, string> = {
    analyzing: 'Analyzing',
    offer_pending: 'Offer sent',
    offer_sent: 'Offer sent',
    under_contract: 'Under contract',
    won: 'Won',
    lost: 'Lost',
    passed: 'Passed',
  };
  return map[s || 'analyzing'] || 'Analyzing';
}

function renderSavedDealRow(d: AnyRecord): string {
  const grade = String(d.investment_grade || 'C');
  const addr = escapeHtml(String(d.address || 'Address unavailable'));
  const city = d.city ? escapeHtml(String(d.city)) : '';
  const price = d.price ? fmtMoneyCompact(Number(d.price)) : '—';
  const cap = d.cap_rate != null ? Number(d.cap_rate).toFixed(1) + '%' : '—';
  const coc = d.cash_on_cash != null ? Number(d.cash_on_cash).toFixed(1) + '%' : '—';
  const cf = Number(d.monthly_cash_flow || 0);
  const listingId = escapeHtml(String(d.listing_id));
  const dealId = escapeHtml(String(d.id));
  const savedAt = d.saved_at ? new Date(String(d.saved_at)).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
  const status = String(d.status || 'analyzing');

  // Wishlist #6: price-drop badge inline with the address when the
  // listing's price has fallen since the user saved it. Threshold:
  // -1% to filter out rounding noise. Positive deltas (price went up)
  // are not surfaced here; they don't change the investor's calculus
  // the same way a drop does.
  const dropPct = d.price_drop_pct != null ? Number(d.price_drop_pct) : null;
  const priceDropBadge = dropPct != null && dropPct <= -1
    ? `<span style="display:inline-flex;align-items:center;gap:3px;font-size:10px;font-weight:800;color:var(--success);background:rgba(0,212,138,0.12);border:1px solid var(--success);padding:1px 6px;border-radius:8px;margin-left:6px">↓ ${Math.abs(dropPct).toFixed(1)}%</span>`
    : '';

  return `
    <div class="portfolio-saved-deal" onclick="openDetail('${listingId}')">
      <div class="portfolio-saved-grade" style="color:${gradeColor(grade)};border-color:${gradeColor(grade)}">
        ${escapeHtml(grade)}
      </div>
      <div class="portfolio-saved-main">
        <div class="portfolio-saved-addr">${addr}${priceDropBadge}</div>
        <div class="portfolio-saved-sub">${city} · ${price}${savedAt ? ' · saved ' + escapeHtml(savedAt) : ''}</div>
        <div class="portfolio-saved-metrics">
          <span>Cap <strong>${cap}</strong></span>
          <span>CoC <strong>${coc}</strong></span>
          <span class="${cf >= 0 ? 'pos' : 'neg'}">CF <strong>${fmtMoney(cf)}/mo</strong></span>
        </div>
      </div>
      <div class="portfolio-saved-right">
        <span class="portfolio-saved-status portfolio-saved-status-${status}">${escapeHtml(dealStatusLabel(status))}</span>
        ${status !== 'won' && status !== 'lost' ? `
          <button class="portfolio-saved-convert" onclick="event.stopPropagation(); convertDealToProperty('${dealId}')" title="I bought this — add to portfolio">
            <i data-feather="check-circle" width="14"></i>
          </button>
        ` : ''}
        <button class="portfolio-saved-delete" onclick="event.stopPropagation(); deleteSavedDeal('${dealId}')" aria-label="Remove saved deal">
          <i data-feather="x" width="14"></i>
        </button>
      </div>
    </div>
  `;
}

export async function convertDealToProperty(dealId: string): Promise<void> {
  if (!confirm('Add this deal to your portfolio? This marks it as "won" and creates an owned property.')) return;
  try {
    haptic();
    await api<AnyRecord>('POST', `/portfolio/deals/${dealId}/convert`);
    showToast('Property added to portfolio!', 'success');
    loadSavedDeals();
    loadPortfolio();
  } catch (err: any) {
    showToast(err.message || 'Could not convert deal', 'error');
  }
}

export async function deleteSavedDeal(dealId: string): Promise<void> {
  if (!confirm('Remove this saved deal?')) return;
  try {
    await api('DELETE', `/portfolio/saved-deals/${dealId}`);
    showToast('Removed', 'success');
    loadSavedDeals();
  } catch (err: any) {
    showToast(err.message || 'Could not remove', 'error');
  }
}

// ─── Formatters ─────────────────────────────────────────────────────────

// Whole dollars, half away from zero — mirrors round_dollars() in
// portfolio_metrics.py so the summed hero figure equals the cards' sum.
function fmtMoney(n: number): string {
  const sign = n >= 0 ? '' : '−';
  return `${sign}$${Math.round(Math.abs(n)).toLocaleString()}`;
}
// Round-then-pick-unit (shared/format compactMagnitude): $999,600 reads
// "$1.0M", never "$1000K". Below $1K it prints whole dollars like fmtMoney.
function fmtMoneyCompact(n: number): string {
  const sign = n >= 0 ? '' : '−';
  return `${sign}$${compactMagnitude(n, { millionDecimals: 1 })}`;
}

// ─── Property list ──────────────────────────────────────────────────────

export function sortPortfolioBy(key: string): void {
  if (!key) return;
  sortKey = (key as typeof sortKey) || 'recent';
  loadPortfolio();
}

// Kept for future use (e.g. filtered portfolio views).
void function strategyLabel(s?: string): string {
  const map: Record<string, string> = {
    buy_and_hold: 'Buy & Hold',
    fix_and_flip: 'Flip',
    brrrr: 'BRRRR',
    short_term_rental: 'STR',
    wholesale: 'Wholesale',
  };
  return map[s || ''] || 'Buy & Hold';
};

void function statusLabel(s?: string): string {
  const map: Record<string, string> = {
    active: 'Active',
    under_rehab: 'Rehab',
    listed_for_sale: 'For sale',
    sold: 'Sold',
    pending_acquisition: 'Pending',
  };
  return map[s || ''] || 'Active';
};

function leaseWarningDays(p: AnyRecord): number | null {
  if (!p.lease_end_date) return null;
  const end = new Date(String(p.lease_end_date));
  const today = new Date();
  const diff = Math.floor((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0 || diff > 60) return null;
  return diff;
}

function renderList(items: AnyRecord[]): void {
  const el = document.getElementById('portfolio-list');
  if (!el) return;

  if (items.length === 0) {
    el.innerHTML = `
      <div class="portfolio-empty">
        <i data-feather="home" width="48"></i>
        <h3>No properties yet</h3>
        <p>Add your first rental to track rent, expenses, and cash flow.</p>
        <button class="btn btn-primary mt-16" onclick="openAddPortfolioModal()">
          <i data-feather="plus" width="16"></i>
          <span>Add Property</span>
        </button>
      </div>
    `;
    if (window.feather) window.feather.replace();
    return;
  }

  // Every card figure comes from the server's `metrics` (the same
  // compute_property_metrics pass whose sum is the dashboard's projected
  // cash flow) — nothing is re-derived client-side, so a card can never
  // disagree with the KPI strip.
  const enriched = items.map((p) => {
    const m = (p.metrics || {}) as AnyRecord;
    return {
      ...p,
      _cash: Number(m.monthly_cash_flow ?? 0),
      _cap: m.cap_rate == null ? null : Number(m.cap_rate),
      // null = no cash invested (no denominator) — the card shows n/a.
      _coc: toNumber(m.cash_on_cash),
      _dscr: m.dscr == null ? null : Number(m.dscr),
      _equity: Number(m.equity ?? 0),
    };
  });

  const sorters: Record<string, (a: AnyRecord, b: AnyRecord) => number> = {
    cash_flow: (a, b) => b._cash - a._cash,
    cap_rate: (a, b) => (b._cap ?? -Infinity) - (a._cap ?? -Infinity),
    equity: (a, b) => b._equity - a._equity,
    recent: (a, b) => (a.created_at < b.created_at ? 1 : -1),
  };
  enriched.sort(sorters[sortKey] || sorters.recent);

  el.innerHTML = enriched.map(renderPropertyCard).join('');
  if (window.feather) window.feather.replace();

  // Reflect current sort in the select.
  const sel = document.getElementById('portfolio-sort-select') as HTMLSelectElement | null;
  if (sel) sel.value = sortKey;

  // Surface active flips (fix_and_flip / brrrr) in a dedicated section above
  // the portfolio feed. Mockup Screen 2 — cost-stack bar + 3-metric.
  renderActiveFlips(
    enriched.filter((p: AnyRecord) => p.strategy === 'fix_and_flip' || p.strategy === 'brrrr') as AnyRecord[],
  );
}

function renderActiveFlips(flips: AnyRecord[]): void {
  const el = document.getElementById('portfolio-active-flips');
  if (!el) return;
  if (flips.length === 0) {
    el.innerHTML = '';
    return;
  }
  el.innerHTML = `
    <div class="portfolio-section-header">
      <h3 class="portfolio-section-head">Active Flips</h3>
    </div>
    ${flips.map(renderFlipCard).join('')}
  `;
  if (window.feather) window.feather.replace();
}

function renderFlipCard(p: AnyRecord): string {
  const id = escapeHtml(String(p.id));
  const addr = escapeHtml(String(p.address || ''));
  const loc = [p.city, p.state].filter(Boolean).join(', ');
  const status = String(p.status || 'active');
  const budget = Number(p.rehab_budget || 0);
  const spent = Number(p.rehab_spent || 0);
  const purchase = Number(p.purchase_price || 0);
  const arv = Number(p.arv || 0);
  const m = (p.metrics || {}) as AnyRecord;
  const daily = toNumber(m.daily_holding_cost) ?? Number(p.daily_holding_cost || 0);

  // Every figure comes from the ONE flip model
  // (portfolio_metrics.flip_profit_breakdown, surfaced on `metrics` as
  // flip_profit / flip_total_in / flip_holding_cost_accrued /
  // flip_roi_pct): ARV − (purchase + closing + max(rehab budget, spent)
  // + holding cost accrued since purchase) − 8% selling costs. The Rehab
  // tab's /profit thermometer reads the same model, so the card's
  // projected profit always equals its `projected_profit`. Nothing is
  // re-derived here. Null (no ARV) → n/a.
  const profitNum = toNumber(m.flip_profit);
  const profit = profitNum ?? 0;
  const totalIn = toNumber(m.flip_total_in) ?? 0;
  const holdingAccrued = toNumber(m.flip_holding_cost_accrued) ?? 0;
  const roiPct = toNumber(m.flip_roi_pct);
  const rehabDonePct = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;

  // Cost-stack bar segments (normalized over ARV or totalIn+profit).
  const denom = Math.max(arv, totalIn + Math.max(0, profit), 1);
  const seg = (value: number, color: string, label: string) => {
    if (value <= 0) return '';
    const pct = (value / denom) * 100;
    return `<div class="flip-stack-seg" style="width:${pct.toFixed(2)}%;background:${color}" title="${escapeHtml(label)}: ${fmtMoney(value)}">${pct > 12 ? escapeHtml(label) : ''}</div>`;
  };

  const statusLabel = status === 'under_rehab' ? 'Under rehab' : status.replace(/_/g, ' ');

  return `
    <div class="flip-dash-card" onclick="openPortfolioDetail('${id}')">
      <div class="flip-dash-head">
        <div>
          <div class="flip-dash-addr">${addr}</div>
          <div class="flip-dash-sub">${escapeHtml(loc)}</div>
        </div>
        <div class="flip-dash-status">${escapeHtml(statusLabel)}</div>
      </div>

      <div class="flip-stack-bar">
        ${seg(purchase, '#6366f1', 'Purchase ' + fmtMoneyCompact(purchase))}
        ${seg(Math.max(spent, budget), '#a78bfa', 'Rehab')}
        ${seg(holdingAccrued, '#fb923c', 'Holding')}
        ${profitNum == null ? '' : seg(profit, profit > 0 ? '#22c55e' : '#ef4444', (profit > 0 ? '+' : '') + fmtMoneyCompact(profit) + ' profit')}
      </div>

      <div class="flip-dash-meta">
        <span>Total in: <strong>${totalIn > 0 ? fmtMoneyCompact(totalIn) : NA}</strong></span>
        <span>ARV: <strong>${arv > 0 ? fmtMoneyCompact(arv) : NA}</strong></span>
      </div>

      <div class="flip-dash-metrics">
        <div class="flip-dash-metric">
          <div class="flip-dash-metric-value">${rehabDonePct.toFixed(0)}%</div>
          <div class="flip-dash-metric-label">Rehab done</div>
        </div>
        <div class="flip-dash-metric warn">
          <div class="flip-dash-metric-value">${fmtMoneyCompact(spent)}/${fmtMoneyCompact(budget)}</div>
          <div class="flip-dash-metric-label">Budget</div>
        </div>
        <div class="flip-dash-metric ${roiPct == null ? '' : roiPct >= 0 ? 'pos' : 'neg'}">
          <div class="flip-dash-metric-value">${roiPct == null ? NA : `${roiPct.toFixed(0)}%`}</div>
          <div class="flip-dash-metric-label">Proj. ROI</div>
        </div>
      </div>

      ${daily > 0 ? `
        <div class="flip-dash-holding">
          <span class="flip-dash-holding-label">Daily holding cost</span>
          <span class="flip-dash-holding-value">${fmtMoney(daily)}/day</span>
          <span class="flip-dash-holding-total">Accrued since purchase: ${fmtMoney(holdingAccrued)}</span>
        </div>
      ` : ''}
    </div>
  `;
}

// Stub for the AI query bar (gap #9 deferred). Opens an info modal so users
// know the feature's planned.
export function openPortfolioAiStub(): void {
  const openModal = (window as AnyRecord as {
    openModal: (t: string, body: string, buttons: AnyRecord[]) => void;
  }).openModal;
  const closeModal = (window as AnyRecord as { closeModal: () => void }).closeModal;
  openModal(
    'Ask about your portfolio',
    `<p style="font-size:14px;color:var(--text-secondary);line-height:1.6;margin:0 0 12px">
      Natural-language queries like <em>"What's my total cash flow in Texas?"</em> or
      <em>"Which property had the worst cap rate?"</em> are coming soon.
    </p>
    <p style="font-size:13px;color:var(--text-muted);margin:0">
      For now, use the <strong>Analytics</strong> screen for portfolio-level insights and
      tap any property for its financials.
    </p>`,
    [{ label: 'OK', style: 'primary', onclick: closeModal }],
  );
}

function renderPropertyCard(p: AnyRecord): string {
  const id = escapeHtml(String(p.id));
  const addr = escapeHtml(String(p.address || ''));
  const locBits = [p.city, p.state].filter(Boolean).join(', ');
  const cash = Number(p._cash || 0);
  const leaseDays = leaseWarningDays(p);

  // Per mockup: 4-metric grid (CAP / CoC / DSCR / EQUITY) — all from the
  // server metrics attached in renderList (same numbers as the detail page).
  const capRate = p._cap == null ? null : Number(p._cap);
  const equity = Math.max(0, Number(p._equity || 0));
  const coc = toNumber(p._coc);
  const dscr = p._dscr == null ? null : Number(p._dscr);

  const capTone = capRate == null ? 'muted' : capRate >= 7 ? 'pos' : capRate >= 5 ? '' : 'neg';
  // Shared 10% / 6% CoC bands (shared/underwriting cashOnCashLevel) mapped
  // to this card's tone classes — same colour the swipe card would show.
  // null (no cash invested → no denominator) is muted n/a, never 0.0%.
  const cocLevel = coc == null ? null : cashOnCashLevel(coc);
  const cocTone = cocLevel == null ? 'muted' : cocLevel === 'green' ? 'pos' : cocLevel === 'yellow' ? '' : 'neg';
  const dscrTone = dscr == null ? 'muted' : dscr >= 1.25 ? 'pos' : dscr >= 1.0 ? 'warn' : 'neg';

  return `
    <div class="portfolio-card">
      <div class="portfolio-card-head">
        <div class="portfolio-card-title">
          <div class="portfolio-card-addr">${addr}</div>
          ${locBits ? `<div class="portfolio-card-sub">${escapeHtml(locBits)}</div>` : ''}
          ${leaseDays != null ? `<div class="portfolio-card-warning">
            <i data-feather="alert-triangle" width="12"></i>
            <span>Lease expires in ${leaseDays} days</span>
          </div>` : ''}
        </div>
        <div class="portfolio-card-cash ${cash >= 0 ? 'pos' : 'neg'}">
          ${fmtMoney(cash)}<span class="portfolio-card-cash-unit">/mo</span>
        </div>
      </div>

      <div class="portfolio-card-metrics">
        <div class="portfolio-card-metric ${capTone}">
          <div class="portfolio-card-metric-value">${capRate == null ? '—' : capRate.toFixed(1) + '%'}</div>
          <div class="portfolio-card-metric-label">Cap rate</div>
        </div>
        <div class="portfolio-card-metric ${cocTone}">
          <div class="portfolio-card-metric-value">${coc == null ? NA : coc.toFixed(1) + '%'}</div>
          <div class="portfolio-card-metric-label">CoC</div>
        </div>
        <div class="portfolio-card-metric ${dscrTone}">
          <div class="portfolio-card-metric-value">${dscr == null ? 'n/a' : dscr.toFixed(2)}</div>
          <div class="portfolio-card-metric-label">DSCR</div>
        </div>
        <div class="portfolio-card-metric">
          <div class="portfolio-card-metric-value">${fmtMoneyCompact(equity)}</div>
          <div class="portfolio-card-metric-label">Equity</div>
        </div>
      </div>

      <div class="portfolio-card-actions">
        <button class="btn btn-secondary btn-sm" onclick="openPortfolioIncomeModal('${id}')">
          <i data-feather="trending-up" width="13"></i>
          <span>Log income</span>
        </button>
        <button class="btn btn-secondary btn-sm" onclick="openPortfolioExpenseModal('${id}')">
          <i data-feather="trending-down" width="13"></i>
          <span>Log expense</span>
        </button>
        <button class="btn btn-secondary btn-sm" onclick="openPortfolioDetail('${id}')">
          <i data-feather="chevron-right" width="13"></i>
          <span>Details</span>
        </button>
      </div>
    </div>
  `;
}

// ═══════════════════════════════════════════════════════════════
// PROPERTY DETAIL SCREEN
// ═══════════════════════════════════════════════════════════════

export function openPortfolioDetail(id: string): void {
  selectedPortfolioId = id;
  selectedPortfolio = null;
  selectedDetailTab = 'overview';
  navigate('portfolioDetail');
}

export function renderPortfolioDetail(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'fade-in screen-padded portfolio-screen';
  div.innerHTML = `
    <div class="portfolio-detail-header">
      <button class="portfolio-back" onclick="navigate('portfolio')">
        <i data-feather="arrow-left" width="18"></i>
      </button>
      <div class="portfolio-detail-heading">
        <div class="portfolio-detail-addr" id="pfd-addr">Loading...</div>
        <div class="portfolio-detail-sub" id="pfd-sub">&nbsp;</div>
      </div>
    </div>

    <div id="pfd-hero" class="portfolio-detail-hero">
      <div class="skeleton" style="height:72px;border-radius:12px"></div>
    </div>

    <!-- Insurance renewal nudge — filled by renderInsuranceNudge() only when
         the saved policy expires within 30 days, isn't dismissed for that
         expiry, and a partner URL is configured. -->
    <div id="pfd-nudge-slot"></div>

    <div class="portfolio-detail-tabs" id="pfd-tabs-bar">
      <button class="portfolio-tab ${selectedDetailTab === 'overview' ? 'active' : ''}" onclick="switchPortfolioTab('overview')">Overview</button>
      <button class="portfolio-tab ${selectedDetailTab === 'financials' ? 'active' : ''}" onclick="switchPortfolioTab('financials')">Financials</button>
      <button class="portfolio-tab ${selectedDetailTab === 'transactions' ? 'active' : ''}" onclick="switchPortfolioTab('transactions')">Transactions</button>
      <button class="portfolio-tab ${selectedDetailTab === 'tax' ? 'active' : ''}" onclick="switchPortfolioTab('tax')">Tax</button>
      <button class="portfolio-tab ${selectedDetailTab === 'capex' ? 'active' : ''}" onclick="switchPortfolioTab('capex')">CapEx</button>
      <button class="portfolio-tab portfolio-tab-flip ${selectedDetailTab === 'rehab' ? 'active' : ''}" onclick="switchPortfolioTab('rehab')" style="display:none" id="pfd-tab-rehab">Rehab</button>
    </div>

    <div id="pfd-tab-content">
      <div class="skeleton-row"><div class="skeleton" style="height:48px;border-radius:10px"></div></div>
    </div>
  `;
  setTimeout(() => {
    loadPortfolioDetail();
    if (window.feather) window.feather.replace();
  }, 50);
  return div;
}

async function loadPortfolioDetail(): Promise<void> {
  if (!selectedPortfolioId) return;
  try {
    const data = await api<AnyRecord>('GET', `/portfolio/properties/${selectedPortfolioId}`);
    selectedPortfolio = data;
    renderDetailHeader(data);
    renderDetailHero(data);
    void renderInsuranceNudge(data);
    renderDetailTab();
  } catch (err: any) {
    const el = document.getElementById('pfd-tab-content');
    if (el) el.innerHTML = `<div class="portfolio-empty"><p>${escapeHtml(err.message || 'Failed to load property')}</p></div>`;
  }
}

function renderDetailHeader(p: AnyRecord): void {
  const addr = document.getElementById('pfd-addr');
  const sub = document.getElementById('pfd-sub');
  if (addr) addr.textContent = String(p.address || '');
  if (sub) {
    const bits = [p.city, p.state, p.zip_code].filter(Boolean).join(', ');
    sub.textContent = bits;
  }
  // Show Rehab tab only for flip/BRRRR strategies.
  const rehabTab = document.getElementById('pfd-tab-rehab');
  const isFlip = p.strategy === 'fix_and_flip' || p.strategy === 'brrrr';
  if (rehabTab) rehabTab.style.display = isFlip ? '' : 'none';
}

function renderDetailHero(p: AnyRecord): void {
  const el = document.getElementById('pfd-hero');
  if (!el) return;
  const m = (p.metrics as AnyRecord) || {};
  const value = Number(m.current_value || p.current_value || p.purchase_price || 0);
  const equity = Number(m.equity || 0);
  const loanBal = Number(m.loan_balance || 0);

  // Debt-free (live loan balance is $0 and equity equals value): the two
  // cards would repeat the same number, so merge them into one card with
  // equity as a muted secondary line. Leveraged properties keep two cards.
  if (loanBal <= 0 && value > 0 && Math.round(equity) === Math.round(value)) {
    el.innerHTML = `
      <div class="portfolio-detail-hero-grid hero-1col">
        <div class="portfolio-detail-hero-cell hero-merged">
          <div class="portfolio-detail-hero-value">${fmtMoneyCompact(value)}</div>
          <div class="portfolio-detail-hero-label">Home value</div>
          <div class="portfolio-detail-hero-sub">Equity ${fmtMoneyCompact(equity)} \u00b7 100%</div>
        </div>
      </div>
    `;
    return;
  }

  // Per mockup Screen 3 — two big cards: Current value + Equity (green).
  el.innerHTML = `
    <div class="portfolio-detail-hero-grid hero-2col">
      <div class="portfolio-detail-hero-cell">
        <div class="portfolio-detail-hero-value">${fmtMoneyCompact(value)}</div>
        <div class="portfolio-detail-hero-label">Current value</div>
      </div>
      <div class="portfolio-detail-hero-cell hero-equity">
        <div class="portfolio-detail-hero-value pos">${fmtMoneyCompact(equity)}</div>
        <div class="portfolio-detail-hero-label">Equity</div>
      </div>
    </div>
  `;
}

// ─── Policy & documents + insurance renewal nudge ───────────────────────
//
// Capture: provider name (free text) + policy expiry (date), saved via
// PUT /portfolio/properties/{id}/insurance (explicit nulls clear it).
// Nudge: the backend computes insurance_nudge_due (0–30 days out, not
// past, not dismissed for this expiry). The card is the same sponsored
// system as the listing-detail CTAs — SPONSORED pill, secondary button,
// disclosure line — and renders nothing when no partner URL is set.

/** "Oct 14, 2026" from an ISO date, parsed as a local calendar date. */
function fmtIsoDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function renderPolicySection(p: AnyRecord): string {
  const id = escapeHtml(String(p.id));
  const provider = p.insurance_provider ? String(p.insurance_provider) : '';
  const expiry = p.insurance_expiry_date ? String(p.insurance_expiry_date) : '';
  const days = p.insurance_days_to_expiry as number | null | undefined;
  let status = '';
  if (expiry && days != null) {
    status = days < 0
      ? `<span class="pfd-policy-status neg">Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago</span>`
      : days === 0
        ? `<span class="pfd-policy-status warn">Expires today</span>`
        : `<span class="pfd-policy-status ${days <= 30 ? 'warn' : ''}">Expires in ${days} day${days === 1 ? '' : 's'}</span>`;
  }
  return `
    <section class="portfolio-detail-section" id="pfd-policy-section">
      <h4>Policy &amp; documents</h4>
      <div class="pfd-policy-intro">Keep your landlord insurance details with the property${expiry ? ` \u00b7 ${status}` : '.'}</div>
      <div class="portfolio-form pfd-policy-form">
        <div class="portfolio-form-row">
          <label>Insurance provider
            <input type="text" id="pfd-ins-provider" maxlength="255" placeholder="e.g. Steadily" value="${escapeHtml(provider)}">
          </label>
          <label>Policy expiry
            <input type="date" id="pfd-ins-expiry" value="${escapeHtml(expiry)}">
          </label>
        </div>
        <div class="pfd-policy-actions">
          <button type="button" class="btn btn-secondary btn-sm" onclick="savePortfolioInsurance('${id}')">Save policy</button>
          ${provider || expiry ? `<button type="button" class="btn btn-ghost btn-sm" onclick="clearPortfolioInsurance('${id}')">Clear</button>` : ''}
        </div>
      </div>
    </section>
  `;
}

async function _putInsurance(propertyId: string, body: { insurance_provider: string | null; insurance_expiry_date: string | null }): Promise<void> {
  const updated = await api<AnyRecord>('PUT', `/portfolio/properties/${encodeURIComponent(propertyId)}/insurance`, body);
  // Keep the in-memory property in sync so the nudge + overview re-render
  // from the server's answer, never from what we hoped we saved.
  if (selectedPortfolio && selectedPortfolio.id === updated.id) selectedPortfolio = { ...selectedPortfolio, ...updated };
  void renderInsuranceNudge(selectedPortfolio || updated);
  if (selectedDetailTab === 'overview') renderDetailTab();
}

export async function savePortfolioInsurance(propertyId: string): Promise<void> {
  const provider = (document.getElementById('pfd-ins-provider') as HTMLInputElement | null)?.value.trim() || null;
  const expiry = (document.getElementById('pfd-ins-expiry') as HTMLInputElement | null)?.value || null;
  try {
    await _putInsurance(propertyId, { insurance_provider: provider, insurance_expiry_date: expiry });
    showToast('Policy saved', 'success');
  } catch (err: any) {
    showToast(err.message || 'Could not save policy', 'error');
  }
}

export async function clearPortfolioInsurance(propertyId: string): Promise<void> {
  try {
    await _putInsurance(propertyId, { insurance_provider: null, insurance_expiry_date: null });
    showToast('Policy cleared', 'success');
  } catch (err: any) {
    showToast(err.message || 'Could not clear policy', 'error');
  }
}

async function renderInsuranceNudge(p: AnyRecord): Promise<void> {
  const slot = document.getElementById('pfd-nudge-slot');
  if (!slot) return;
  if (!p.insurance_nudge_due || !p.insurance_expiry_date) {
    slot.innerHTML = '';
    return;
  }
  const url = await getProviderCtaUrl('insurance_renewal');
  // Re-check after the await: the user may have navigated or dismissed.
  const live = document.getElementById('pfd-nudge-slot');
  if (!live || !url) {
    if (live) live.innerHTML = '';
    return;
  }
  const id = String(p.id);
  const address = String(p.address || 'this property');
  live.innerHTML = `
    <div class="provider-cta-card pfd-nudge-card" data-provider="insurance">
      <span class="provider-cta-pill">Sponsored</span>
      <div class="provider-cta-head">
        <span class="provider-cta-icon"><i data-feather="shield" width="16"></i></span>
        <div>
          <div class="provider-cta-title">Insurance renewal due</div>
          <div class="provider-cta-body">Your landlord insurance for ${escapeHtml(address)} expires on ${escapeHtml(fmtIsoDate(String(p.insurance_expiry_date)))} \u2014 want a fresh quote?</div>
        </div>
      </div>
      <!-- Sponsored CTAs never use .btn-primary — that's reserved for real app actions. -->
      <a class="btn btn-secondary btn-block provider-cta-btn" id="pfd-nudge-btn" target="_blank" rel="noopener noreferrer sponsored">
        Get a renewal quote
        <i data-feather="external-link" width="14"></i>
      </a>
      <div class="provider-cta-foot">
        <div class="provider-cta-disclosure">${escapeHtml(SPONSORED_DISCLOSURE)}</div>
        <button type="button" class="pfd-nudge-dismiss" onclick="dismissInsuranceNudge('${escapeHtml(id)}')" aria-label="Dismiss renewal reminder">Dismiss</button>
      </div>
    </div>
  `;
  const btn = live.querySelector<HTMLAnchorElement>('#pfd-nudge-btn');
  if (btn) {
    btn.href = url; // DOM property, never string-interpolated into markup
    btn.addEventListener('click', () => {
      trackEvent('provider_cta_click', { placement: 'portfolio_renewal', provider: 'insurance', property_id: id });
    });
  }
  if (window.feather) window.feather.replace();
}

export async function dismissInsuranceNudge(propertyId: string): Promise<void> {
  try {
    const updated = await api<AnyRecord>('POST', `/portfolio/properties/${encodeURIComponent(propertyId)}/insurance/dismiss-nudge`);
    if (selectedPortfolio && selectedPortfolio.id === updated.id) selectedPortfolio = { ...selectedPortfolio, ...updated };
    const slot = document.getElementById('pfd-nudge-slot');
    if (slot) slot.innerHTML = '';
    showToast("Reminder dismissed until your next policy", 'info');
  } catch (err: any) {
    showToast(err.message || 'Could not dismiss', 'error');
  }
}

const DETAIL_TABS = ['overview', 'financials', 'transactions', 'tax', 'capex', 'rehab'] as const;

export function switchPortfolioTab(tab: string): void {
  if (!DETAIL_TABS.includes(tab as typeof DETAIL_TABS[number])) return;
  haptic(15);
  selectedDetailTab = tab as typeof selectedDetailTab;
  document.querySelectorAll('.portfolio-detail-tabs .portfolio-tab').forEach((btn, i) => {
    btn.classList.toggle('active', DETAIL_TABS[i] === tab);
  });
  renderDetailTab();
}

function renderDetailTab(): void {
  const el = document.getElementById('pfd-tab-content');
  if (!el || !selectedPortfolio) return;
  if (selectedDetailTab === 'overview') renderOverviewTab(el, selectedPortfolio);
  else if (selectedDetailTab === 'financials') renderFinancialsTab(el, selectedPortfolio);
  else if (selectedDetailTab === 'transactions') renderTransactionsTab(el);
  else if (selectedDetailTab === 'tax') renderTaxTab(el, selectedPortfolio);
  else if (selectedDetailTab === 'capex') renderCapexTab(el, selectedPortfolio);
  else renderRehabTab(el, selectedPortfolio);
}

// ── Overview tab ────────────────────────────────────────────────────────

function renderOverviewTab(el: HTMLElement, p: AnyRecord): void {
  const m = (p.metrics as AnyRecord) || {};
  const rows: Array<[string, string]> = [];

  // Property detail rows
  const detail = [
    ['Bedrooms', p.bedrooms],
    ['Bathrooms', p.bathrooms],
    ['Sqft', p.sqft ? Number(p.sqft).toLocaleString() : null],
    ['Year built', p.year_built],
    ['Property type', p.property_type ? String(p.property_type).replace(/_/g, ' ') : null],
    ['Units', p.units],
  ];
  detail.forEach(([k, v]) => {
    if (v != null && v !== '') rows.push([k as string, String(v)]);
  });

  // Acquisition
  const acq: Array<[string, any]> = [
    ['Purchase price', p.purchase_price ? fmtMoney(Number(p.purchase_price)) : null],
    ['Purchase date', p.purchase_date],
    ['Closing costs', p.closing_costs ? fmtMoney(Number(p.closing_costs)) : null],
    ['Initial rehab', p.initial_rehab_cost ? fmtMoney(Number(p.initial_rehab_cost)) : null],
  ];

  // Financing
  const fin: Array<[string, any]> = [
    ['Loan amount', p.loan_amount ? fmtMoney(Number(p.loan_amount)) : null],
    ['Interest rate', p.interest_rate ? `${Number(p.interest_rate).toFixed(2)}%` : null],
    ['Term', p.loan_term_months ? `${Math.round(Number(p.loan_term_months) / 12)} yr` : null],
    ['Loan type', p.loan_type ? String(p.loan_type).replace(/_/g, ' ') : null],
  ];

  // Tenancy
  const ten: Array<[string, any]> = [
    ['Tenant', p.tenant_name],
    ['Lease start', p.lease_start_date],
    ['Lease end', p.lease_end_date],
  ];

  // Tiered like the Deal Analysis tab: three heroes at full emphasis,
  // the remaining nine in a quieter secondary grid.
  const dscr = dscrDisplay(m.dscr);
  // Every tile carries a plain-English caption derived from its value
  // (shared/glosses.ts), same pattern as the Deal Analysis tab.
  const noi = Number(m.noi || 0);
  const rent = Number(m.monthly_rent || p.monthly_rent || 0);
  // rent_source: "actual" = trailing-12 calendar-month average of logged
  // income (collections already embed vacancy); "stated" = the property's
  // monthly_rent × (1 − vacancy). Cash flow / NOI use the same basis.
  const rentActual = m.rent_source === 'actual';
  const rentBasis = rentActual ? 'Actual \u00b7 trailing 12 mo' : 'Stated';
  const value = Number(m.current_value || 0);
  const loanBal = Number(m.loan_balance || 0);
  // cash_on_cash / ltv / roi_total_pct are null when there is no
  // denominator (no cash invested, no value, …) — muted n/a, never 0%.
  const coc = toNumber(m.cash_on_cash);
  const ltv = toNumber(m.ltv);
  const grm = m.grm == null ? null : Number(m.grm);
  const cap = m.cap_rate == null ? null : Number(m.cap_rate);
  const cashInv = Number(m.cash_invested || 0);
  const dep = Number(m.annual_depreciation || 0);
  const roi = toNumber(m.roi_total_pct);
  const breakeven = Number(m.breakeven_occupancy || 0);
  const metricGrid = `
    <div class="portfolio-hero-metrics">
      ${heroCell('NOI', fmtMoney(noi), '', '/yr', glossNoi(noi, rent))}
      ${heroCell('Cap rate', cap == null ? '\u2014' : `${cap.toFixed(2)}%`, cap == null ? 'tone-muted' : toneFor(cap, 7, 5), '', glossCapRate(cap))}
      ${heroCell('Cash on cash', pctTile(coc, 2), coc == null ? 'tone-muted' : toneFor(coc, 10, 6), '', glossCashOnCash(coc))}
    </div>
    <div class="portfolio-metric-note" role="group" aria-label="Rent basis: ${escapeHtml(rentBasis)}">
      Rent <strong>${fmtMoney(rent)}/mo</strong> \u00b7 <span class="portfolio-metric-note-tag${rentActual ? ' actual' : ''}">${escapeHtml(rentBasis)}</span>
      \u2014 ${rentActual ? 'average of logged income; cash flow and NOI use the same basis.' : 'stated rent less vacancy; log income to switch to actuals.'}
    </div>
    <div class="portfolio-metric-grid secondary">
      ${metricCell('DSCR', dscr.value, dscr.tone, glossDscr(dscr.num, 'No loan to cover'))}
      ${metricCell('LTV', pctTile(ltv, 1), ltv == null ? 'tone-muted' : '', ltv == null ? 'No value on file' : glossLtv(ltv, loanBal))}
      ${metricCell('GRM', grm == null ? '\u2014' : grm.toFixed(1), grm == null ? 'tone-muted' : '', glossGrm(grm))}
      ${metricCell('Loan balance', fmtMoneyCompact(loanBal), '', glossLoanBalance(loanBal, value))}
      ${metricCell('Cash invested', fmtMoneyCompact(cashInv), '', glossCashInvested(cashInv, Number(p.purchase_price || 0)))}
      ${metricCell('Annual depreciation', fmtMoney(dep) + '/yr', '', glossDepreciation(dep, Number(m.depreciable_basis || 0)))}
      ${metricCell('Tax savings est', fmtMoney(Number(m.tax_savings_estimate || 0)) + '/yr', '', glossTaxSavings(Number(m.tax_savings_estimate || 0), Number(m.tax_bracket_pct || 0), dep))}
      ${metricCell('Total ROI', pctTile(roi, 2), roi == null ? 'tone-muted' : toneFor(roi, 12, 6), roi == null ? 'No cash invested to measure against' : glossTotalRoi(Number(m.roi_total_dollars || 0), cashInv))}
      ${metricCell('Break-even occ.', `${breakeven.toFixed(1)}%`, breakeven > 100 ? 'tone-neg' : '', glossBreakeven(breakeven, rent))}
    </div>
  `;

  el.innerHTML = `
    <section class="portfolio-detail-section">
      <h4>Key metrics</h4>
      ${metricGrid}
    </section>

    <div id="pfd-climate-slot"></div>
    <div id="pfd-partners-slot"></div>

    <section class="portfolio-detail-section">
      <h4>Property</h4>
      ${tableRows(rows)}
    </section>

    ${acq.some((r) => r[1]) ? `<section class="portfolio-detail-section">
      <h4>Acquisition</h4>
      ${tableRows(acq.filter((r) => r[1] != null) as Array<[string, string]>)}
    </section>` : ''}

    ${fin.some((r) => r[1]) ? `<section class="portfolio-detail-section">
      <h4>Financing</h4>
      ${tableRows(fin.filter((r) => r[1] != null) as Array<[string, string]>)}
    </section>` : ''}

    ${ten.some((r) => r[1]) ? `<section class="portfolio-detail-section">
      <h4>Tenancy</h4>
      ${tableRows(ten.filter((r) => r[1] != null) as Array<[string, string]>)}
    </section>` : ''}

    ${renderPolicySection(p)}

    ${p.notes ? `<section class="portfolio-detail-section">
      <h4>Notes</h4>
      <p class="portfolio-detail-notes">${escapeHtml(String(p.notes))}</p>
    </section>` : ''}
  `;

  // Load climate + partners asynchronously (additive, won't break if API fails).
  loadClimateBlock(String(p.id));
  loadPartnersBlock(String(p.id));
}

async function loadClimateBlock(propertyId: string): Promise<void> {
  const slot = document.getElementById('pfd-climate-slot');
  if (!slot) return;
  try {
    const r = await api<AnyRecord>('GET', `/portfolio/properties/${propertyId}/climate`);
    const tier = String(r.overall_risk || 'unknown');
    const tierLabel: Record<string, string> = {
      low: 'Low', moderate: 'Moderate', high: 'High', extreme: 'Extreme', unknown: 'Unknown',
    };
    const items: Array<[string, string]> = [
      ['Flood', String(r.flood?.tier || 'unknown')],
      ['Fire', String(r.fire?.tier || 'unknown')],
      ['Environmental', String(r.environmental?.tier || 'unknown')],
    ];
    const note = !r.has_coordinates
      ? 'Add latitude/longitude to this property to pull FEMA flood + USDA wildfire data.'
      : '';
    slot.innerHTML = `
      <section class="portfolio-detail-section climate-section climate-${tier}">
        <h4>Climate + environmental risk</h4>
        <div class="climate-hero">
          <div class="climate-hero-tier climate-tier-${tier}">${escapeHtml(tierLabel[tier])}</div>
          <div class="climate-hero-label">Overall risk</div>
        </div>
        <div class="climate-grid">
          ${items.map(([label, t]) => `
            <div class="climate-cell climate-tier-${t}">
              <div class="climate-cell-label">${escapeHtml(label)}</div>
              <div class="climate-cell-value">${escapeHtml(tierLabel[t] || t)}</div>
            </div>
          `).join('')}
        </div>
        ${note ? `<div class="climate-note">${escapeHtml(note)}</div>` : ''}
      </section>
    `;
  } catch {
    /* silent */
  }
}

async function loadPartnersBlock(propertyId: string): Promise<void> {
  const slot = document.getElementById('pfd-partners-slot');
  if (!slot) return;
  try {
    const year = new Date().getFullYear();
    const r = await api<AnyRecord>('GET', `/portfolio/properties/${propertyId}/splits?year=${year}`);
    const items = (r.items as AnyRecord[]) || [];
    const alloc = r.allocation as AnyRecord | null;
    const ownerPct = Number(r.owner_pct || 100);

    const partnerRows = items.map((s) => `
      <div class="partner-row">
        <div class="partner-row-main">
          <div class="partner-name">${escapeHtml(String(s.partner_name))}</div>
          <div class="partner-sub">${escapeHtml(String(s.partner_role || 'partner'))}${s.partner_email ? ' · ' + escapeHtml(String(s.partner_email)) : ''}</div>
        </div>
        <div class="partner-pct">${Number(s.equity_pct).toFixed(1)}%</div>
        <button class="portfolio-tx-delete" onclick="deleteSplit('${escapeHtml(String(s.id))}')" aria-label="Remove">
          <i data-feather="x" width="12"></i>
        </button>
      </div>
    `).join('');

    const allocTable = alloc ? `
      <div style="margin-top:12px;font-size:11px;font-weight:700;color:var(--primary);text-transform:uppercase;letter-spacing:0.05em;margin-bottom:6px">${year} allocation</div>
      <table class="portfolio-pnl-table">
        <tr><th>Partner</th><th class="right">Share</th><th class="right">Income</th><th class="right">Net</th></tr>
        ${(alloc.partners as AnyRecord[]).map((p) => `
          <tr>
            <td>${escapeHtml(String(p.label))}</td>
            <td class="right">${Number(p.equity_pct).toFixed(1)}%</td>
            <td class="right">${fmtMoney(Number(p.income))}</td>
            <td class="right ${Number(p.net) >= 0 ? 'pos' : 'neg'}"><strong>${fmtMoney(Number(p.net))}</strong></td>
          </tr>
        `).join('')}
      </table>
    ` : '';

    slot.innerHTML = `
      <section class="portfolio-detail-section">
        <h4>Ownership + partners</h4>
        <div class="partner-owner-bar">
          <div class="partner-owner-fill" style="width:${ownerPct.toFixed(1)}%"></div>
          <div class="partner-owner-label">You ${ownerPct.toFixed(1)}% · Partners ${(100 - ownerPct).toFixed(1)}%</div>
        </div>
        <button class="btn btn-secondary btn-sm" onclick="openPartnerModal('${escapeHtml(propertyId)}')" style="margin-top:10px">
          <i data-feather="plus" width="13"></i><span>Add partner</span>
        </button>
        ${items.length ? `<div class="partner-list" style="margin-top:10px">${partnerRows}</div>` : ''}
        ${allocTable}
      </section>
    `;
    if (window.feather) window.feather.replace();
  } catch {
    /* silent */
  }
}

export function openPartnerModal(propertyId: string): void {
  const openModal = (window as AnyRecord as {
    openModal: (t: string, body: string, buttons: AnyRecord[]) => void;
  }).openModal;
  const closeModal = (window as AnyRecord as { closeModal: () => void }).closeModal;
  const body = `
    <div class="portfolio-form">
      <label>Partner name<input type="text" id="pn-name" required></label>
      <div class="portfolio-form-row">
        <label>Equity %<input type="number" step="0.5" id="pn-pct" min="0" max="100" required></label>
        <label>Role
          <select id="pn-role">
            <option value="passive_investor">Passive investor</option>
            <option value="general_partner">General partner</option>
            <option value="co_owner">Co-owner</option>
            <option value="limited_partner">Limited partner</option>
            <option value="family">Family</option>
          </select>
        </label>
      </div>
      <label>Email (optional)<input type="email" id="pn-email"></label>
      <label>Notes<textarea id="pn-notes" rows="2"></textarea></label>
    </div>
  `;
  openModal('Add partner', body, [
    { label: 'Cancel', style: 'secondary', onclick: closeModal },
    { label: 'Add', style: 'primary', onclick: () => submitPartner(propertyId) },
  ]);
}

async function submitPartner(propertyId: string): Promise<void> {
  const getV = (id: string) => (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value?.trim() || '';
  const name = getV('pn-name');
  const pct = Number(getV('pn-pct'));
  if (!name || !pct || pct <= 0) {
    showToast('Name and equity % are required', 'error');
    return;
  }
  try {
    await api('POST', `/portfolio/properties/${propertyId}/splits`, {
      partner_name: name,
      equity_pct: pct,
      partner_role: getV('pn-role') || null,
      partner_email: getV('pn-email') || null,
      notes: getV('pn-notes') || null,
    });
    (window as AnyRecord).closeModal?.();
    showToast('Partner added', 'success');
    loadPartnersBlock(propertyId);
  } catch (err: any) {
    showToast(err.message || 'Could not add partner', 'error');
  }
}

export async function deleteSplit(splitId: string): Promise<void> {
  if (!confirm('Remove this partner?')) return;
  try {
    await api('DELETE', `/portfolio/splits/${splitId}`);
    showToast('Removed', 'success');
    // Reload the partners block using the currently-selected property.
    if (selectedPortfolioId) loadPartnersBlock(selectedPortfolioId);
  } catch (err: any) {
    showToast(err.message || 'Could not remove', 'error');
  }
}

function metricCell(label: string, value: string, tone: string = '', gloss = ''): string {
  return `
    <div class="portfolio-metric-cell ${tone}">
      <div class="portfolio-metric-value">${escapeHtml(value)}</div>
      <div class="portfolio-metric-label">${escapeHtml(label)}</div>
      ${gloss ? `<div class="portfolio-metric-gloss">${escapeHtml(gloss)}</div>` : ''}
    </div>
  `;
}

/** Hero tile for the tiered Key metrics row (NOI / cap rate / cash-on-cash). */
function heroCell(label: string, value: string, tone: string = '', unit = '', gloss = ''): string {
  return `
    <div class="portfolio-hero-cell ${tone}">
      <div class="portfolio-hero-value">${escapeHtml(value)}${unit ? `<span class="portfolio-hero-unit">${escapeHtml(unit)}</span>` : ''}</div>
      <div class="portfolio-hero-label">${escapeHtml(label)}</div>
      ${gloss ? `<div class="portfolio-metric-gloss">${escapeHtml(gloss)}</div>` : ''}
    </div>
  `;
}

/** Percent tile for a ratio the backend reports as null when it has no
 *  denominator (cash_on_cash, ltv, roi_total_pct). shared/tiles.ts
 *  toNumber + NA: null / NaN → "n/a", never "0.00%" or "NaN%". */
function pctTile(v: unknown, digits: number): string {
  const n = toNumber(v);
  return n == null ? NA : `${n.toFixed(digits)}%`;
}

/** DSCR is undefined without debt: the backend sends null when the loan
 *  balance or debt service is zero. Render that as muted "n/a", never as
 *  a red 0.00 — a debt-free property isn't "failing coverage". */
function dscrDisplay(raw: unknown): { value: string; tone: string; num: number | null } {
  const n = raw == null || raw === '' ? NaN : Number(raw);
  if (!Number.isFinite(n)) return { value: 'n/a', tone: 'tone-muted', num: null };
  return { value: n.toFixed(2), tone: toneFor(n, 1.25, 1.0), num: n };
}

function toneFor(value: number, greenAt: number, yellowAt: number): string {
  if (value >= greenAt) return 'tone-pos';
  if (value >= yellowAt) return 'tone-warn';
  return 'tone-neg';
}

function tableRows(rows: Array<[string, string]>): string {
  return `
    <table class="portfolio-detail-table">
      ${rows.map(([k, v]) => `<tr><td>${escapeHtml(k)}</td><td>${escapeHtml(v)}</td></tr>`).join('')}
    </table>
  `;
}

// ── Financials tab (P&L) ────────────────────────────────────────────────

async function renderFinancialsTab(el: HTMLElement, p: AnyRecord): Promise<void> {
  if (!selectedPortfolioId) return;
  el.innerHTML = `<div style="color:var(--text-secondary);font-size:13px;padding:12px 0">Loading P&L...</div>`;
  const year = new Date().getFullYear();
  try {
    const pnl = await api<AnyRecord>('GET', `/portfolio/properties/${selectedPortfolioId}/pnl?year=${year}&period=quarterly`);
    const inc = (pnl.income || {}) as Record<string, number>;
    const exp = (pnl.expenses || {}) as Record<string, number>;
    const timeline = (pnl.timeline as AnyRecord[]) || [];
    const totalIncome = Number(pnl.total_income || 0);
    const totalExpenses = Number(pnl.total_expenses || 0);
    const net = Number(pnl.net_income || 0);

    const categoryList = (obj: Record<string, number>, emptyLabel: string) => {
      const entries = Object.entries(obj);
      if (entries.length === 0) return `<div class="portfolio-pnl-empty">${escapeHtml(emptyLabel)}</div>`;
      return `<table class="portfolio-pnl-table">${entries
        .map(([k, v]) => `<tr><td>${escapeHtml(k)}</td><td class="right"><strong>${fmtMoney(Number(v))}</strong></td></tr>`)
        .join('')}</table>`;
    };

    const quarterRows = timeline.length
      ? `<table class="portfolio-pnl-table">
          <tr><th>Period</th><th class="right">Income</th><th class="right">Expenses</th><th class="right">Net</th></tr>
          ${timeline.map((t) => `<tr>
            <td>${escapeHtml(String(t.period))}</td>
            <td class="right">${fmtMoney(Number(t.income || 0))}</td>
            <td class="right">${fmtMoney(Number(t.expenses || 0))}</td>
            <td class="right ${Number(t.net || 0) >= 0 ? 'pos' : 'neg'}"><strong>${fmtMoney(Number(t.net || 0))}</strong></td>
          </tr>`).join('')}
         </table>`
      : `<div class="portfolio-pnl-empty">No transactions yet this year.</div>`;

    el.innerHTML = `
      <section class="portfolio-detail-section">
        <h4>${year} summary</h4>
        <div class="portfolio-pnl-summary">
          <div><div class="portfolio-pnl-summary-label">Income</div><div class="portfolio-pnl-summary-value pos">${fmtMoney(totalIncome)}</div></div>
          <div><div class="portfolio-pnl-summary-label">Expenses</div><div class="portfolio-pnl-summary-value neg">${fmtMoney(totalExpenses)}</div></div>
          <div><div class="portfolio-pnl-summary-label">Net</div><div class="portfolio-pnl-summary-value ${net >= 0 ? 'pos' : 'neg'}">${fmtMoney(net)}</div></div>
        </div>
      </section>

      <section class="portfolio-detail-section">
        <h4>By quarter</h4>
        ${quarterRows}
      </section>

      <section class="portfolio-detail-section">
        <h4>Income by category</h4>
        ${categoryList(inc, 'No income logged yet.')}
      </section>

      <section class="portfolio-detail-section">
        <h4>Expenses by category</h4>
        ${categoryList(exp, 'No expenses logged yet.')}
      </section>
    `;
  } catch (err: any) {
    el.innerHTML = `<div class="portfolio-empty"><p>${escapeHtml(err.message || 'Failed to load P&L')}</p></div>`;
  }
  void p;
}

// ── Transactions tab ────────────────────────────────────────────────────

async function renderTransactionsTab(el: HTMLElement): Promise<void> {
  if (!selectedPortfolioId) return;
  el.innerHTML = `<div style="color:var(--text-secondary);font-size:13px;padding:12px 0">Loading transactions...</div>`;
  try {
    const data = await api<AnyRecord>('GET', `/portfolio/properties/${selectedPortfolioId}/transactions`);
    const items = (data.items as AnyRecord[]) || [];

    const id = escapeHtml(String(selectedPortfolioId));
    const header = `
      <div class="portfolio-tx-actions">
        <button class="btn btn-primary btn-sm" onclick="openPortfolioIncomeModal('${id}')">
          <i data-feather="trending-up" width="13"></i><span>Income</span>
        </button>
        <button class="btn btn-secondary btn-sm" onclick="openPortfolioExpenseModal('${id}')">
          <i data-feather="trending-down" width="13"></i><span>Expense</span>
        </button>
      </div>
    `;

    if (items.length === 0) {
      el.innerHTML = `${header}
        <div class="portfolio-empty">
          <i data-feather="list" width="36"></i>
          <p>No transactions yet. Log rent or an expense to build your history.</p>
        </div>
      `;
      if (window.feather) window.feather.replace();
      return;
    }

    const rows = items.map((t) => {
      const isInc = t.type === 'income';
      const amountClass = isInc ? 'pos' : 'neg';
      const sign = isInc ? '+' : '−';
      return `
        <div class="portfolio-tx-row">
          <div class="portfolio-tx-icon ${amountClass}">
            <i data-feather="${isInc ? 'trending-up' : 'trending-down'}" width="14"></i>
          </div>
          <div class="portfolio-tx-main">
            <div class="portfolio-tx-cat">${escapeHtml(String(t.category || ''))}</div>
            <div class="portfolio-tx-sub">${escapeHtml(String(t.description || t.tenant_name || ''))} · ${escapeHtml(String(t.date))}</div>
          </div>
          <div class="portfolio-tx-amount ${amountClass}">
            ${sign}${fmtMoney(Math.abs(Number(t.amount || 0)))}
          </div>
          <button class="portfolio-tx-delete" onclick="deletePortfolioTransaction('${escapeHtml(String(t.id))}')" aria-label="Delete">
            <i data-feather="x" width="12"></i>
          </button>
        </div>
      `;
    }).join('');

    el.innerHTML = `${header}<div class="portfolio-tx-list">${rows}</div>`;
    if (window.feather) window.feather.replace();
  } catch (err: any) {
    el.innerHTML = `<div class="portfolio-empty"><p>${escapeHtml(err.message || 'Failed to load transactions')}</p></div>`;
  }
}

export async function deletePortfolioTransaction(txId: string): Promise<void> {
  if (!confirm('Delete this transaction?')) return;
  try {
    await api('DELETE', `/portfolio/transactions/${txId}`);
    showToast('Deleted', 'success');
    renderDetailTab();
  } catch (err: any) {
    showToast(err.message || 'Could not delete', 'error');
  }
}

// ═══════════════════════════════════════════════════════════════
// ADD PROPERTY MODAL (unchanged from Phase 3D)
// ═══════════════════════════════════════════════════════════════

export function openAddPortfolioModal(): void {
  const openModal = (window as AnyRecord as {
    openModal: (t: string, body: string, buttons: AnyRecord[]) => void;
  }).openModal;
  const closeModal = (window as AnyRecord as { closeModal: () => void }).closeModal;
  if (!openModal) return;

  const body = `
    <div class="portfolio-form">
      <div class="portfolio-form-row" style="align-items:flex-end">
        <label style="flex:1">Address
          <input type="text" id="pf-address" placeholder="123 Rental Lane" required>
        </label>
        <button type="button" class="btn btn-secondary btn-sm" id="pf-lookup-btn" onclick="lookupPropertyAutofill()">Look up</button>
      </div>
      <div id="pf-lookup-status" style="font-size:13px;margin:-2px 0 8px;min-height:18px" aria-live="polite"></div>
      <div class="portfolio-form-row">
        <label>City<input type="text" id="pf-city" placeholder="Houston"></label>
        <label>State<input type="text" id="pf-state" maxlength="2" placeholder="TX" style="text-transform:uppercase"></label>
        <label>Zip<input type="text" id="pf-zip" placeholder="77018"></label>
      </div>
      <div class="portfolio-form-row">
        <label>Type
          <select id="pf-ptype">
            <option value="">—</option>
            <option value="single_family">Single family</option>
            <option value="condo">Condo</option>
            <option value="townhouse">Townhouse</option>
            <option value="multi_family">Multi-family</option>
            <option value="land">Land</option>
          </select>
        </label>
        <label>Beds<input type="number" id="pf-beds" min="0" max="50" step="1" placeholder="3"></label>
        <label>Baths<input type="number" id="pf-baths" min="0" max="50" step="0.5" placeholder="2"></label>
      </div>
      <div class="portfolio-form-row">
        <label>Living sqft<input type="number" id="pf-sqft" min="0" placeholder="1800"></label>
        <label>Year built<input type="number" id="pf-year" min="1700" max="2100" placeholder="2005"></label>
      </div>
      <div class="portfolio-form-row">
        <label>Strategy
          <select id="pf-strategy">
            <option value="buy_and_hold">Buy & Hold</option>
            <option value="fix_and_flip">Fix & Flip</option>
            <option value="brrrr">BRRRR</option>
            <option value="short_term_rental">Short-term Rental</option>
          </select>
        </label>
        <label>Status
          <select id="pf-status">
            <option value="active">Active</option>
            <option value="under_rehab">Under rehab</option>
            <option value="listed_for_sale">Listed for sale</option>
            <option value="pending_acquisition">Pending acquisition</option>
          </select>
        </label>
      </div>
      <div class="portfolio-form-row">
        <label>Purchase price<input type="number" id="pf-pp" placeholder="$" min="0"></label>
        <label>Current value<input type="number" id="pf-cv" placeholder="$" min="0"></label>
      </div>
      <label>Monthly rent
        <input type="number" id="pf-rent" placeholder="$ /mo" min="0">
      </label>
      <div class="portfolio-form-row">
        <label>Mortgage P&amp;I<input type="number" id="pf-mort" placeholder="$ /mo, principal + interest only" min="0" aria-describedby="pf-mort-hint"></label>
        <label>Tax<input type="number" id="pf-tax" placeholder="$ /mo" min="0"></label>
      </div>
      <div class="portfolio-form-hint" id="pf-mort-hint">Principal + interest only — escrowed taxes and insurance go in their own fields below.</div>
      <div class="portfolio-form-row">
        <label>Insurance<input type="number" id="pf-ins" placeholder="$ /mo" min="0"></label>
        <label>HOA<input type="number" id="pf-hoa" placeholder="$ /mo" min="0"></label>
      </div>
      <label>Notes
        <textarea id="pf-notes" rows="2" placeholder="Optional"></textarea>
      </label>
    </div>
  `;
  openModal('Add property', body, [
    { label: 'Cancel', style: 'secondary', onclick: closeModal },
    { label: 'Add', style: 'primary', onclick: submitAddPortfolio },
  ]);
}

async function submitAddPortfolio(): Promise<void> {
  const getVal = (id: string) =>
    (document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null)?.value?.trim() || '';
  const getNum = (id: string) => {
    const v = getVal(id);
    return v ? Number(v) : null;
  };
  const address = getVal('pf-address');
  if (!address) {
    showToast('Address is required', 'error');
    return;
  }
  const payload: AnyRecord = {
    address,
    city: getVal('pf-city') || null,
    state: (getVal('pf-state') || '').toUpperCase() || null,
    zip_code: getVal('pf-zip') || null,
    property_type: getVal('pf-ptype') || null,
    bedrooms: getNum('pf-beds'),
    bathrooms: getNum('pf-baths'),
    sqft: getNum('pf-sqft'),
    year_built: getNum('pf-year'),
    strategy: getVal('pf-strategy') || 'buy_and_hold',
    status: getVal('pf-status') || 'active',
    purchase_price: getNum('pf-pp'),
    current_value: getNum('pf-cv'),
    monthly_rent: getNum('pf-rent'),
    mortgage_payment: getNum('pf-mort'),
    property_tax_monthly: getNum('pf-tax'),
    insurance_monthly: getNum('pf-ins'),
    hoa_monthly: getNum('pf-hoa'),
    notes: getVal('pf-notes') || null,
  };
  Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);

  try {
    await api('POST', '/portfolio/properties', payload);
    (window as AnyRecord).closeModal?.();
    showToast('Property added', 'success');
    loadPortfolio();
  } catch (err: any) {
    showToast(err.message || 'Could not add property', 'error');
  }
}

// ═══════════════════════════════════════════════════════════════
// ADDRESS AUTO-FILL — public-record lookup for the Add Property form
// ═══════════════════════════════════════════════════════════════

function applyAutofill(auto: AnyRecord): void {
  const setVal = (id: string, v: unknown) => {
    if (v === null || v === undefined || v === '') return;
    const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
    if (!el) return;
    el.value = String(v);
    el.style.borderColor = '#16a34a';
  };
  setVal('pf-address', auto.address);
  setVal('pf-city', auto.city);
  setVal('pf-state', auto.state);
  setVal('pf-zip', auto.zip_code);
  setVal('pf-ptype', auto.property_type);
  setVal('pf-beds', auto.bedrooms);
  setVal('pf-baths', auto.bathrooms);
  setVal('pf-sqft', auto.sqft);
  setVal('pf-year', auto.year_built);
  setVal('pf-cv', auto.current_value);
  setVal('pf-tax', auto.property_tax_monthly);
  const sugg = Number(auto._suggested_purchase_price);
  if (sugg > 0) {
    const pp = document.getElementById('pf-pp') as HTMLInputElement | null;
    if (pp && !pp.value) pp.placeholder = `Last sold $${Math.round(sugg).toLocaleString('en-US')}`;
  }
}

export async function lookupPropertyAutofill(): Promise<void> {
  const addrEl = document.getElementById('pf-address') as HTMLInputElement | null;
  const statusEl = document.getElementById('pf-lookup-status');
  const btn = document.getElementById('pf-lookup-btn') as HTMLButtonElement | null;
  const address = addrEl?.value.trim() ?? '';
  if (!address) {
    showToast('Enter an address first', 'error');
    addrEl?.focus();
    return;
  }
  if (btn) { btn.disabled = true; btn.textContent = 'Looking up…'; }
  if (statusEl) statusEl.innerHTML = '<span style="color:#6b7280">Searching public records…</span>';
  try {
    const res = await api<AnyRecord>('POST', '/portfolio/properties/lookup', { address });
    const auto = (res.auto_filled || {}) as AnyRecord;
    if (res.status === 'duplicate' && res.duplicate_property_id) {
      if (statusEl) {
        statusEl.innerHTML =
          '<span style="color:#b45309">This address is already in your portfolio.</span> ' +
          `<button type="button" class="portfolio-link-btn" onclick="closeModal();openPortfolioDetail('${res.duplicate_property_id}')">View property</button>`;
      }
      return;
    }
    applyAutofill(auto);
    const filledCount = ['city', 'state', 'zip_code', 'property_type', 'bedrooms', 'bathrooms', 'sqft', 'year_built', 'current_value', 'property_tax_monthly']
      .filter((k) => auto[k] !== null && auto[k] !== undefined && auto[k] !== '').length;
    const disclaimers = Array.isArray(res.disclaimers) ? (res.disclaimers as string[]) : [];
    const ok = res.status === 'success';
    const headline = ok
      ? `✓ Auto-filled ${filledCount} fields from public records — review and edit before adding.`
      : String(res.message || 'Lookup unavailable. Enter details manually.');
    const disc = disclaimers.length
      ? `<div style="margin-top:4px;font-size:12px;color:#6b7280">${disclaimers.map((d) => escapeHtml(d)).join('<br>')}</div>`
      : '';
    if (statusEl) {
      statusEl.innerHTML = `<span style="color:${ok ? '#16a34a' : '#b45309'}">${escapeHtml(headline)}</span>${disc}`;
    }
  } catch (err: any) {
    if (statusEl) statusEl.innerHTML = '<span style="color:#b45309">Lookup failed. Enter details manually.</span>';
    showToast(err.message || 'Lookup failed', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Look up'; }
  }
}

// ═══════════════════════════════════════════════════════════════
// DELETE + LOG INCOME + LOG EXPENSE (unchanged API)
// ═══════════════════════════════════════════════════════════════

export async function deletePortfolioProperty(id: string): Promise<void> {
  if (!confirm('Remove this property from your portfolio?')) return;
  try {
    await api('DELETE', `/portfolio/properties/${id}`);
    showToast('Removed', 'success');
    loadPortfolio();
  } catch (err: any) {
    showToast(err.message || 'Could not remove property', 'error');
  }
}

export function openPortfolioIncomeModal(propertyId: string): void {
  const openModal = (window as AnyRecord as {
    openModal: (t: string, body: string, buttons: AnyRecord[]) => void;
  }).openModal;
  const closeModal = (window as AnyRecord as { closeModal: () => void }).closeModal;
  const today = new Date().toISOString().slice(0, 10);
  const body = `
    <div class="portfolio-form">
      <label>Category
        <select id="pf-inc-cat">
          <option value="rent">Rent</option>
          <option value="late_fees">Late fees</option>
          <option value="pet_deposit">Pet deposit</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label>Amount
        <input type="number" id="pf-inc-amount" placeholder="$" min="0" required>
      </label>
      <label>Date
        <input type="date" id="pf-inc-date" value="${today}">
      </label>
      <label>Tenant (optional)
        <input type="text" id="pf-inc-tenant" placeholder="e.g. Jordan Mitchell">
      </label>
    </div>
  `;
  openModal('Log income', body, [
    { label: 'Cancel', style: 'secondary', onclick: closeModal },
    { label: 'Save', style: 'primary', onclick: () => submitPortfolioIncome(propertyId) },
  ]);
}

async function submitPortfolioIncome(propertyId: string): Promise<void> {
  const cat = (document.getElementById('pf-inc-cat') as HTMLSelectElement | null)?.value || 'rent';
  const amount = Number((document.getElementById('pf-inc-amount') as HTMLInputElement | null)?.value || 0);
  const date = (document.getElementById('pf-inc-date') as HTMLInputElement | null)?.value;
  const tenant = (document.getElementById('pf-inc-tenant') as HTMLInputElement | null)?.value?.trim();
  if (!amount || amount <= 0 || !date) {
    showToast('Amount and date are required', 'error');
    return;
  }
  try {
    await api('POST', '/portfolio/income', {
      portfolio_property_id: propertyId,
      category: cat,
      amount,
      date,
      tenant_name: tenant || null,
    });
    (window as AnyRecord).closeModal?.();
    haptic();
    showToast('Income logged', 'success');
    if (selectedPortfolioId === propertyId) renderDetailTab();
    else loadPortfolio();
  } catch (err: any) {
    showToast(err.message || 'Could not log income', 'error');
  }
}

export function openPortfolioExpenseModal(propertyId: string): void {
  const openModal = (window as AnyRecord as {
    openModal: (t: string, body: string, buttons: AnyRecord[]) => void;
  }).openModal;
  const closeModal = (window as AnyRecord as { closeModal: () => void }).closeModal;
  const today = new Date().toISOString().slice(0, 10);
  const body = `
    <div class="portfolio-form">
      <label>Category
        <select id="pf-exp-cat">
          <option value="maintenance">Maintenance</option>
          <option value="repairs">Repairs</option>
          <option value="utilities">Utilities</option>
          <option value="management">Management</option>
          <option value="insurance">Insurance</option>
          <option value="taxes">Taxes</option>
          <option value="mortgage">Mortgage</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label>Amount
        <input type="number" id="pf-exp-amount" placeholder="$" min="0" required>
      </label>
      <label>Date
        <input type="date" id="pf-exp-date" value="${today}">
      </label>
      <label>Description
        <input type="text" id="pf-exp-desc" placeholder="Optional">
      </label>
    </div>
  `;
  openModal('Log expense', body, [
    { label: 'Cancel', style: 'secondary', onclick: closeModal },
    { label: 'Save', style: 'primary', onclick: () => submitPortfolioExpense(propertyId) },
  ]);
}

async function submitPortfolioExpense(propertyId: string): Promise<void> {
  const cat = (document.getElementById('pf-exp-cat') as HTMLSelectElement | null)?.value || 'other';
  const amount = Number((document.getElementById('pf-exp-amount') as HTMLInputElement | null)?.value || 0);
  const date = (document.getElementById('pf-exp-date') as HTMLInputElement | null)?.value;
  const desc = (document.getElementById('pf-exp-desc') as HTMLInputElement | null)?.value?.trim();
  if (!amount || amount <= 0 || !date) {
    showToast('Amount and date are required', 'error');
    return;
  }
  try {
    await api('POST', '/portfolio/expenses', {
      portfolio_property_id: propertyId,
      category: cat,
      amount,
      date,
      description: desc || null,
    });
    (window as AnyRecord).closeModal?.();
    haptic();
    showToast('Expense logged', 'success');
    if (selectedPortfolioId === propertyId) renderDetailTab();
    else loadPortfolio();
  } catch (err: any) {
    showToast(err.message || 'Could not log expense', 'error');
  }
}

// Quick-tx from Dashboard: picks a property first.
export function openPortfolioQuickTx(): void {
  const openModal = (window as AnyRecord as {
    openModal: (t: string, body: string, buttons: AnyRecord[]) => void;
  }).openModal;
  const closeModal = (window as AnyRecord as { closeModal: () => void }).closeModal;
  openModal(
    'Log transaction',
    `<p style="color:var(--text-secondary);font-size:14px;margin:0 0 12px">Open a property to log income or expenses for it.</p>`,
    [{ label: 'OK', style: 'primary', onclick: closeModal }],
  );
}

// ═══════════════════════════════════════════════════════════════
// DEAL ANALYZER MODAL (Phase 1D)
// ═══════════════════════════════════════════════════════════════

let analyzerDebounce: ReturnType<typeof setTimeout> | null = null;

export function openDealAnalyzer(): void {
  const openModal = (window as AnyRecord as {
    openModal: (t: string, body: string, buttons: AnyRecord[]) => void;
  }).openModal;
  const closeModal = (window as AnyRecord as { closeModal: () => void }).closeModal;
  if (!openModal) return;

  const body = `
    <div class="deal-analyzer">
      <div class="deal-analyzer-inputs">
        <div class="deal-analyzer-row">
          <label>Purchase price<input type="number" id="da-price" value="250000" min="1000" oninput="onDealAnalyzerInput()"></label>
          <label>Monthly rent<input type="number" id="da-rent" value="2400" min="0" oninput="onDealAnalyzerInput()"></label>
        </div>
        <div class="deal-analyzer-row">
          <label>Down pmt %<input type="number" id="da-dp" value="25" min="0" max="100" oninput="onDealAnalyzerInput()"></label>
          <label>Rate %<input type="number" step="0.125" id="da-rate" value="7.5" min="0" max="25" oninput="onDealAnalyzerInput()"></label>
        </div>
        <div class="deal-analyzer-row">
          <label>Loan term
            <select id="da-term" onchange="onDealAnalyzerInput()">
              <option value="360" selected>30 yr</option>
              <option value="180">15 yr</option>
            </select>
          </label>
          <label>Strategy
            <select id="da-strategy" onchange="onDealAnalyzerInput()">
              <option value="buy_and_hold" selected>Buy & Hold</option>
              <option value="brrrr">BRRRR</option>
              <option value="fix_and_flip">Fix & Flip</option>
              <option value="short_term_rental">Short-term Rental</option>
            </select>
          </label>
        </div>
        <div class="deal-analyzer-row">
          <label>State<input type="text" id="da-state" maxlength="2" value="TX" style="text-transform:uppercase" oninput="onDealAnalyzerInput()"></label>
          <label>Closing %<input type="number" step="0.5" id="da-closing" value="3" min="0" max="15" oninput="onDealAnalyzerInput()"></label>
        </div>
        <div class="deal-analyzer-row">
          <label>Vacancy %<input type="number" step="0.5" id="da-vac" value="8" min="0" max="50" oninput="onDealAnalyzerInput()"></label>
          <label>Mgmt %<input type="number" step="0.5" id="da-mgmt" value="8" min="0" max="30" oninput="onDealAnalyzerInput()"></label>
        </div>
        <div class="deal-analyzer-row">
          <label>Maintenance %<input type="number" step="0.5" id="da-maint" value="5" min="0" max="30" oninput="onDealAnalyzerInput()"></label>
          <label>Rehab budget<input type="number" id="da-rehab" value="0" min="0" oninput="onDealAnalyzerInput()"></label>
        </div>
        <div class="deal-analyzer-row">
          <label>ARV (flip)<input type="number" id="da-arv" value="0" min="0" placeholder="After-repair value" oninput="onDealAnalyzerInput()"></label>
          <label>&nbsp;<div style="height:34px"></div></label>
        </div>
      </div>

      <div class="deal-analyzer-output" id="da-output">
        <div style="color:var(--text-secondary);font-size:13px;padding:8px 0">Adjust inputs to compute deal...</div>
      </div>
    </div>
  `;
  openModal('Deal Analyzer', body, [
    { label: 'Close', style: 'secondary', onclick: closeModal },
  ]);
  // Kick off first calc.
  setTimeout(onDealAnalyzerInput, 50);
}

export function onDealAnalyzerInput(): void {
  if (analyzerDebounce) clearTimeout(analyzerDebounce);
  analyzerDebounce = setTimeout(runDealAnalysis, 220);
}

async function runDealAnalysis(): Promise<void> {
  const el = document.getElementById('da-output');
  if (!el) return;
  const getN = (id: string) => {
    const v = (document.getElementById(id) as HTMLInputElement | null)?.value;
    return v ? Number(v) : null;
  };
  const getV = (id: string) => (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value || '';

  const price = getN('da-price');
  if (!price || price < 1000) {
    el.innerHTML = '<div class="deal-analyzer-warn">Enter a purchase price to analyze.</div>';
    return;
  }

  const payload: AnyRecord = {
    purchase_price: price,
    monthly_rent: getN('da-rent') ?? undefined,
    down_payment_pct: getN('da-dp') ?? undefined,
    interest_rate: getN('da-rate') ?? undefined,
    loan_term_months: getN('da-term') ?? undefined,
    closing_costs_pct: getN('da-closing') ?? undefined,
    vacancy_rate: getN('da-vac') ?? undefined,
    property_mgmt_pct: getN('da-mgmt') ?? undefined,
    maintenance_pct: getN('da-maint') ?? undefined,
    rehab_estimate: getN('da-rehab') ?? undefined,
    arv: (getN('da-arv') || 0) > 0 ? getN('da-arv') : undefined,
    strategy: getV('da-strategy') || 'buy_and_hold',
    state: (getV('da-state') || '').toUpperCase() || undefined,
  };
  Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

  el.innerHTML = '<div class="deal-analyzer-loading">Calculating...</div>';
  try {
    const r = await api<AnyRecord>('POST', '/portfolio/deals/analyze', payload);
    renderDealAnalyzerOutput(el, r);
  } catch (err: any) {
    el.innerHTML = `<div class="deal-analyzer-warn">${escapeHtml(err.message || 'Could not compute')}</div>`;
  }
}

function passFailChip(label: string, pass: boolean): string {
  return `<span class="deal-chip ${pass ? 'pass' : 'fail'}">${escapeHtml(label)}${pass ? ' ✓' : ' ✗'}</span>`;
}

function renderDealAnalyzerOutput(el: HTMLElement, r: AnyRecord): void {
  const cap = Number(r.cap_rate || 0);
  const coc = Number(r.cash_on_cash_return || 0);
  const dscr = dscrDisplay(r.dscr_ratio);
  const cashFlow = (r.cash_flow || {}) as AnyRecord;
  const cf = Number(cashFlow.monthly_pre_tax || 0);
  const cfAnnual = Number(cashFlow.annual_pre_tax || cf * 12);
  const noi = Number(r.noi || 0);
  const grm = Number(r.gross_rent_multiplier || 0);
  const score = Number(r.investment_score || 0);
  const verdict = String(r.deal_verdict || '');
  const cashNeeded = Number(cashFlow.total_cash_invested || 0);
  // Break-even occupancy — debt service + operating / gross rent.
  const gross = Number(r.gross_annual_rent || 0);
  const mortgage = (r.mortgage || {}) as AnyRecord;
  const opEx = ((r.operating_expenses || {}) as AnyRecord).total || 0;
  const breakeven = gross > 0 ? ((Number(mortgage.annual_debt_service || 0) + Number(opEx)) / gross * 100) : 0;
  const flip = r.flip_overlay as AnyRecord | undefined;

  const tone = cf >= 0 ? 'tone-pos' : 'tone-neg';

  el.innerHTML = `
    <div class="deal-hero">
      <div class="deal-hero-score">
        <div class="deal-hero-score-value">${score}</div>
        <div class="deal-hero-score-label">${escapeHtml(verdict.replace(/_/g, ' '))}</div>
      </div>
      <div class="deal-hero-cf ${tone}">
        ${fmtMoney(cf)}<span>/mo</span>
        <div class="deal-hero-cf-label">Monthly cash flow</div>
      </div>
    </div>

    <div class="deal-chips-row">
      ${passFailChip(`Cap ≥ 7%`, cap >= 7)}
      ${dscr.num == null ? `<span class="deal-chip na">DSCR n/a (no loan)</span>` : passFailChip(`DSCR ≥ 1.25`, dscr.num >= 1.25)}
      ${passFailChip(`CoC ≥ 10%`, cashOnCashLevel(coc) === 'green')}
      ${passFailChip(`Cash flow > 0`, cf > 0)}
    </div>

    <div class="deal-metric-grid">
      ${metricCell('Cap rate', `${cap.toFixed(2)}%`, toneFor(cap, 7, 5))}
      ${metricCell('Cash on cash', `${coc.toFixed(2)}%`, toneFor(coc, 10, 6))}
      ${metricCell('DSCR', dscr.value, dscr.tone)}
      ${metricCell('NOI', fmtMoney(noi) + '/yr')}
      ${metricCell('GRM', grm.toFixed(1))}
      ${metricCell('Cash needed', fmtMoneyCompact(cashNeeded))}
      ${metricCell('Annual cash flow', fmtMoney(cfAnnual))}
      ${metricCell('Break-even occ.', `${breakeven.toFixed(1)}%`)}
    </div>

    ${flip ? `
    <section class="deal-flip">
      <h4>Flip overlay</h4>
      <div class="deal-flip-grid">
        ${metricCell('ARV', fmtMoneyCompact(Number(flip.arv || 0)))}
        ${metricCell('Total in', fmtMoneyCompact(Number(flip.total_in || 0)))}
        ${metricCell('Selling costs', fmtMoney(Number(flip.selling_costs || 0)))}
        ${metricCell('Projected profit', fmtMoney(Number(flip.projected_profit || 0)), Number(flip.projected_profit || 0) > 0 ? 'tone-pos' : 'tone-neg')}
        ${metricCell('ROI', `${Number(flip.roi_pct || 0).toFixed(2)}%`)}
      </div>
    </section>
    ` : ''}
  `;
}

// ═══════════════════════════════════════════════════════════════
// 1031 EXCHANGES (Phase 2B)
// ═══════════════════════════════════════════════════════════════

let ownedPropsCache: AnyRecord[] = [];

// "Filed a return extension" toggle. The 180-day exchange period is also
// capped by the due date of the seller's tax return for the year of the
// sale (Apr 15) unless that return is extended; the backend recomputes
// `effective_closing_deadline` / `days_to_close` when the list is read
// with ?return_extension_filed=true, so the toggle refetches.
let exchangeReturnExtensionFiled = false;

function exchangesQuery(): string {
  return exchangeReturnExtensionFiled ? '?return_extension_filed=true' : '';
}

export function toggleExchangeReturnExtension(checked: boolean): void {
  exchangeReturnExtensionFiled = !!checked;
  void loadExchanges();
}

function renderExchangeExtensionToggle(): string {
  return `
    <label class="ex-extension-toggle">
      <input type="checkbox" id="ex-return-extension"${exchangeReturnExtensionFiled ? ' checked' : ''}
             onchange="toggleExchangeReturnExtension(this.checked)">
      <span>Filed a return extension</span>
      <span class="ex-extension-hint">Lifts the tax-return cap so the full 180 days apply</span>
    </label>
  `;
}

export async function loadExchanges(): Promise<void> {
  const el = document.getElementById('portfolio-exchanges');
  if (!el) return;
  try {
    const [exData, propData] = await Promise.all([
      api<AnyRecord>('GET', `/portfolio/exchanges${exchangesQuery()}`),
      api<AnyRecord>('GET', '/portfolio'),
    ]);
    ownedPropsCache = (propData.items as AnyRecord[]) || [];
    const items = (exData.items as AnyRecord[]) || [];
    if (items.length === 0) {
      el.innerHTML = `
        <div class="portfolio-saved-empty">
          <i data-feather="refresh-cw" width="18"></i>
          <span>No active 1031 exchanges. Start one to defer capital gains on your next sale.</span>
        </div>
      `;
      if (window.feather) window.feather.replace();
      return;
    }
    el.innerHTML = renderExchangeExtensionToggle()
      + items.map(renderExchangeCard).join('')
      + complianceFooter(DISCLAIMER.exchange);
    if (window.feather) window.feather.replace();
  } catch (err: any) {
    sectionUnavailable('portfolio-exchanges', '1031 exchanges', 'loadExchanges()', err);
  }
}

/** §1031 identification-rule chip (backend `identification_status`). */
function identificationChip(status: unknown): string {
  const map: Record<string, { label: string; cls: string }> = {
    three_property: { label: '3-property rule', cls: 'ok' },
    two_hundred_pct: { label: '200% rule', cls: 'ok' },
    ninety_five_pct_required: { label: '95% rule required', cls: 'warn' },
    cannot_evaluate: { label: 'Add values to evaluate', cls: 'muted' },
  };
  const hit = map[String(status || '')];
  if (!hit) return '';
  return `<span class="ex-ident-chip ${hit.cls}" title="IRS \u00a71031 identification rule in effect">${escapeHtml(hit.label)}</span>`;
}

function urgencyClass(days: number | null, expired: boolean): string {
  if (expired) return 'ex-urgent-expired';
  if (days == null) return '';
  if (days <= 7) return 'ex-urgent-critical';
  if (days <= 14) return 'ex-urgent-warn';
  return 'ex-urgent-ok';
}

function exchangeStatusLabel(s: string): string {
  const map: Record<string, string> = {
    planning: 'Planning',
    property_sold: 'Sold',
    identifying: 'Identifying',
    under_contract: 'Under contract',
    completed: 'Completed',
    failed: 'Failed',
  };
  return map[s] || s;
}

function renderExchangeCard(e: AnyRecord): string {
  const idDays = e.days_to_id;
  const closeDays = e.days_to_close;
  const idCls = urgencyClass(idDays, e.id_expired);
  const closeCls = urgencyClass(closeDays, e.close_expired);
  const deferred = Number(e.gain_deferred || 0);
  const addr = escapeHtml(String(e.relinquished_address || 'Unlinked property'));
  const ident = (e.identified_properties || []) as AnyRecord[];

  const idBlock = e.identification_deadline
    ? `<div class="ex-countdown ${idCls}">
         <div class="ex-countdown-label">45-day ID</div>
         <div class="ex-countdown-value">${e.id_expired ? 'Expired' : (idDays != null ? `${idDays}d` : '—')}</div>
       </div>`
    : '';
  // days_to_close counts to `effective_closing_deadline` — the earlier of
  // sale + 180 days and the tax-return due date (unless an extension was
  // filed). When the return date is the binding one, say so.
  const cappedByReturn = !!e.closing_deadline_capped_by_return;
  const effectiveClose = e.effective_closing_deadline ? String(e.effective_closing_deadline) : (e.closing_deadline ? String(e.closing_deadline) : '');
  const closeBlock = e.closing_deadline
    ? `<div class="ex-countdown ${closeCls}">
         <div class="ex-countdown-label">${cappedByReturn ? 'Close by' : '180-day close'}</div>
         <div class="ex-countdown-value">${e.close_expired ? 'Expired' : (closeDays != null ? `${closeDays}d` : '—')}</div>
         ${effectiveClose ? `<div class="ex-countdown-date">${escapeHtml(fmtIsoDate(effectiveClose))}</div>` : ''}
       </div>`
    : '';
  const cappedNote = cappedByReturn
    ? `<div class="ex-deadline-note">Capped by tax-return due date${e.tax_return_due_date ? ` (${escapeHtml(fmtIsoDate(String(e.tax_return_due_date)))})` : ''} \u2014 the 180-day date is ${e.closing_deadline ? escapeHtml(fmtIsoDate(String(e.closing_deadline))) : 'later'}. File an extension to use the full window.</div>`
    : '';
  const identChip = identificationChip(e.identification_status);

  return `
    <div class="ex-card" onclick="openExchangeModal('${escapeHtml(String(e.id))}')">
      <div class="ex-card-head">
        <div>
          <div class="ex-card-addr">${addr}</div>
          <div class="ex-card-sub">Status: <strong>${escapeHtml(exchangeStatusLabel(String(e.status || '')))}</strong></div>
        </div>
        <div class="ex-deferred">
          <div class="ex-deferred-label">Gain deferred</div>
          <div class="ex-deferred-value">${fmtMoneyCompact(deferred)}</div>
        </div>
      </div>
      <div class="ex-countdowns">${idBlock}${closeBlock}</div>
      ${cappedNote}
      ${ident.length ? `<div class="ex-identified">Identified: ${ident.map((p) => escapeHtml(String(p.address || 'property'))).join(' · ')} ${identChip}</div>` : ''}
    </div>
  `;
}

// ─── Modal: create / edit / view exchange ───────────────────────────────

let editingExchangeId: string | null = null;

export function openExchangeModal(exchangeId?: string): void {
  const openModal = (window as AnyRecord as {
    openModal: (t: string, body: string, buttons: AnyRecord[]) => void;
  }).openModal;
  const closeModal = (window as AnyRecord as { closeModal: () => void }).closeModal;

  editingExchangeId = exchangeId || null;

  const propOptions = ownedPropsCache
    .map((p) => `<option value="${escapeHtml(String(p.id))}">${escapeHtml(String(p.address || ''))}</option>`)
    .join('');

  const body = `
    <div class="portfolio-form">
      <label>Relinquished property
        <select id="ex-prop">
          <option value="">— Select owned property —</option>
          ${propOptions}
        </select>
      </label>
      <div class="portfolio-form-row">
        <label>Sale price<input type="number" id="ex-sale-price" min="0" placeholder="$"></label>
        <label>Sale date<input type="date" id="ex-sale-date"></label>
      </div>
      <div class="portfolio-form-row">
        <label>Adjusted basis<input type="number" id="ex-basis" min="0" placeholder="$"></label>
        <label>Accum. depreciation<input type="number" id="ex-dep" min="0" placeholder="$"></label>
      </div>
      <label>Status
        <select id="ex-status">
          <option value="planning">Planning</option>
          <option value="property_sold">Property sold</option>
          <option value="identifying">Identifying</option>
          <option value="under_contract">Under contract</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
      </label>
      <div class="portfolio-form-row">
        <label>Qualified intermediary<input type="text" id="ex-qi" placeholder="e.g. First American"></label>
        <label>QI contact<input type="text" id="ex-qi-contact" placeholder="Email or phone"></label>
      </div>
      <label>State rate % (for tax comparison)
        <input type="number" step="0.1" id="ex-state-rate" min="0" max="20" placeholder="5.0">
      </label>
      <label>Notes<textarea id="ex-notes" rows="2"></textarea></label>

      <div id="ex-compare-slot" style="margin-top:12px"></div>

      ${exchangeId ? `
      <div class="ex-ident-section">
        <div class="ex-ident-head">Identified properties (${'<span id="ex-ident-count">0</span>'}) <span id="ex-ident-status"></span></div>
        <div id="ex-ident-list"></div>
        <label style="margin-top:6px">Add address<input type="text" id="ex-new-addr" placeholder="123 Main St, Austin"></label>
        <div class="portfolio-form-row" style="margin-top:6px">
          <label>Asking price<input type="number" id="ex-new-price" min="0" placeholder="$"></label>
          <label>Estimated value<input type="number" id="ex-new-value" min="0" placeholder="$ FMV for the 200% rule"></label>
        </div>
        <button class="btn btn-secondary btn-sm" onclick="addIdentifiedProperty()" style="margin-top:6px">+ Add candidate</button>
      </div>
      ` : ''}
    </div>
  `;

  const buttons: AnyRecord[] = [
    { label: 'Cancel', style: 'secondary', onclick: closeModal },
    { label: 'Compute tax', style: 'secondary', onclick: computeExchangeComparison },
    { label: exchangeId ? 'Save' : 'Create', style: 'primary', onclick: submitExchange },
  ];
  if (exchangeId) {
    buttons.splice(1, 0, {
      label: 'Delete',
      style: 'danger',
      onclick: deleteExchange,
    });
  }

  openModal(exchangeId ? 'Edit 1031 Exchange' : 'New 1031 Exchange', body, buttons);

  // Load existing exchange data into the form.
  if (exchangeId) {
    setTimeout(() => loadExchangeIntoForm(exchangeId), 50);
  }
}

async function loadExchangeIntoForm(id: string): Promise<void> {
  try {
    const e = await api<AnyRecord>('GET', `/portfolio/exchanges/${id}${exchangesQuery()}`);
    const set = (sel: string, v: any) => {
      const el = document.getElementById(sel) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (el && v != null) el.value = String(v);
    };
    set('ex-prop', e.relinquished_property_id || '');
    set('ex-sale-price', e.sale_price);
    set('ex-sale-date', e.sale_date);
    set('ex-basis', e.adjusted_basis);
    set('ex-status', e.status);
    set('ex-qi', e.qualified_intermediary);
    set('ex-qi-contact', e.qi_contact);
    set('ex-notes', e.notes);
    renderIdentifiedList(e.identified_properties || [], e.identification_status);
  } catch (err: any) {
    showToast(err.message || 'Could not load exchange', 'error');
  }
}

function renderIdentifiedList(list: AnyRecord[], identificationStatus?: unknown): void {
  const el = document.getElementById('ex-ident-list');
  const count = document.getElementById('ex-ident-count');
  const statusEl = document.getElementById('ex-ident-status');
  if (!el) return;
  if (count) count.textContent = String(list.length);
  if (statusEl) statusEl.innerHTML = identificationChip(identificationStatus);
  if (list.length === 0) {
    el.innerHTML = `<div style="color:var(--text-muted);font-size:12px;padding:4px 0">None yet.</div>`;
    return;
  }
  el.innerHTML = list.map((p, i) => `
    <div class="ex-ident-row">
      <div class="ex-ident-addr">${escapeHtml(String(p.address || ''))}</div>
      <div class="ex-ident-price">${p.estimated_value ? fmtMoneyCompact(Number(p.estimated_value)) : p.price ? fmtMoneyCompact(Number(p.price)) : '\u2014'}</div>
      <button class="portfolio-tx-delete" onclick="removeIdentifiedProperty(${i})" aria-label="Remove">
        <i data-feather="x" width="12"></i>
      </button>
    </div>
  `).join('');
  if (window.feather) window.feather.replace();
}

export async function addIdentifiedProperty(): Promise<void> {
  if (!editingExchangeId) return;
  const addr = (document.getElementById('ex-new-addr') as HTMLInputElement | null)?.value?.trim();
  const price = (document.getElementById('ex-new-price') as HTMLInputElement | null)?.value;
  const value = (document.getElementById('ex-new-value') as HTMLInputElement | null)?.value;
  if (!addr) {
    showToast('Address required', 'error');
    return;
  }
  try {
    // estimated_value is the FMV the 200% / 95% rules are evaluated on;
    // the backend falls back to `price` when it is omitted.
    const r = await api<AnyRecord>('POST', `/portfolio/exchanges/${editingExchangeId}/identify`, {
      address: addr,
      price: price ? Number(price) : null,
      estimated_value: value ? Number(value) : null,
    });
    renderIdentifiedList(r.identified_properties || [], r.identification_status);
    const a = document.getElementById('ex-new-addr') as HTMLInputElement | null;
    const p = document.getElementById('ex-new-price') as HTMLInputElement | null;
    const v = document.getElementById('ex-new-value') as HTMLInputElement | null;
    if (a) a.value = '';
    if (p) p.value = '';
    if (v) v.value = '';
  } catch (err: any) {
    showToast(err.message || 'Could not add', 'error');
  }
}

export async function removeIdentifiedProperty(index: number): Promise<void> {
  if (!editingExchangeId) return;
  try {
    const r = await api<AnyRecord>('DELETE', `/portfolio/exchanges/${editingExchangeId}/identify/${index}`);
    renderIdentifiedList(r.identified_properties || [], r.identification_status);
  } catch (err: any) {
    showToast(err.message || 'Could not remove', 'error');
  }
}

export async function computeExchangeComparison(): Promise<void> {
  const slot = document.getElementById('ex-compare-slot');
  if (!slot) return;
  const price = Number((document.getElementById('ex-sale-price') as HTMLInputElement | null)?.value || 0);
  const basis = Number((document.getElementById('ex-basis') as HTMLInputElement | null)?.value || 0);
  const dep = Number((document.getElementById('ex-dep') as HTMLInputElement | null)?.value || 0);
  const stateRate = Number((document.getElementById('ex-state-rate') as HTMLInputElement | null)?.value || 0);
  if (!price || !basis) {
    showToast('Enter sale price + adjusted basis to compare', 'error');
    return;
  }
  slot.innerHTML = '<div style="color:var(--text-muted);font-size:12px">Calculating...</div>';
  try {
    const r = await api<AnyRecord>('POST', '/portfolio/exchanges/compare', {
      sale_price: price,
      adjusted_basis: basis,
      accumulated_depreciation: dep,
      state_rate_pct: stateRate || null,
      niit_applies: true,
    });
    slot.innerHTML = `
      <div class="ex-compare">
        <div class="ex-compare-head">Exchange vs. pay tax</div>
        <div class="ex-compare-grid">
          <div class="ex-compare-cell neg">
            <div class="ex-compare-label">If you sell</div>
            <div class="ex-compare-value">${fmtMoney(Number(r.net_proceeds_if_sold || 0))}</div>
            <div class="ex-compare-sub">${fmtMoney(Number(r.total_tax_if_sold || 0))} tax</div>
          </div>
          <div class="ex-compare-cell pos">
            <div class="ex-compare-label">If you exchange</div>
            <div class="ex-compare-value">${fmtMoney(Number(r.net_proceeds_if_exchanged || 0))}</div>
            <div class="ex-compare-sub">${fmtMoney(Number(r.tax_deferred || 0))} deferred</div>
          </div>
        </div>
        <div class="ex-compare-breakdown">
          <div><span>Federal cap gains</span><strong>${fmtMoney(Number(r.federal_capital_gains_tax || 0))}</strong></div>
          <div><span>Depreciation recapture</span><strong>${fmtMoney(Number(r.depreciation_recapture_tax || 0))}</strong></div>
          <div><span>NIIT (3.8%)</span><strong>${fmtMoney(Number(r.niit || 0))}</strong></div>
          <div><span>State tax</span><strong>${fmtMoney(Number(r.state_tax || 0))}</strong></div>
        </div>
      </div>
    `;
  } catch (err: any) {
    slot.innerHTML = `<div style="color:var(--warning);font-size:12px">${escapeHtml(err.message || 'Calc failed')}</div>`;
  }
}

async function submitExchange(): Promise<void> {
  const getV = (id: string) => (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value?.trim() || '';
  const getN = (id: string) => {
    const v = getV(id);
    return v ? Number(v) : null;
  };
  const payload: AnyRecord = {
    relinquished_property_id: getV('ex-prop') || null,
    sale_price: getN('ex-sale-price'),
    sale_date: getV('ex-sale-date') || null,
    adjusted_basis: getN('ex-basis'),
    status: getV('ex-status') || 'planning',
    qualified_intermediary: getV('ex-qi') || null,
    qi_contact: getV('ex-qi-contact') || null,
    notes: getV('ex-notes') || null,
  };
  Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);

  try {
    if (editingExchangeId) {
      await api('PUT', `/portfolio/exchanges/${editingExchangeId}`, payload);
      showToast('Exchange updated', 'success');
    } else {
      await api('POST', '/portfolio/exchanges', payload);
      showToast('Exchange created', 'success');
    }
    (window as AnyRecord).closeModal?.();
    loadExchanges();
  } catch (err: any) {
    showToast(err.message || 'Could not save', 'error');
  }
}

async function deleteExchange(): Promise<void> {
  if (!editingExchangeId) return;
  if (!confirm('Delete this exchange? This cannot be undone.')) return;
  try {
    await api('DELETE', `/portfolio/exchanges/${editingExchangeId}`);
    (window as AnyRecord).closeModal?.();
    showToast('Exchange deleted', 'success');
    loadExchanges();
  } catch (err: any) {
    showToast(err.message || 'Could not delete', 'error');
  }
}

// ═══════════════════════════════════════════════════════════════
// TAX TAB — depreciation schedule (Phase 2A)
// ═══════════════════════════════════════════════════════════════

/**
 * Marginal bracket for tax-savings estimates — mirrors the backend's
 * _user_tax_bracket (routes/portfolio.py): a positive
 * investment_criteria.tax_bracket_pct wins, otherwise 32%. `preferred`
 * is a bracket the backend already resolved for this payload, if any.
 */
function resolveTaxBracketPct(preferred?: unknown): number {
  const fromPayload = toNumber(preferred);
  if (fromPayload != null && fromPayload > 0) return fromPayload;
  const criteria = (state.user?.investment_criteria as AnyRecord | undefined) || {};
  const saved = toNumber(criteria.tax_bracket_pct);
  return saved != null && saved > 0 ? saved : 32;
}

async function renderTaxTab(el: HTMLElement, p: AnyRecord): Promise<void> {
  el.innerHTML = `<div style="color:var(--text-secondary);font-size:13px;padding:12px 0">Loading tax...</div>`;
  try {
    const year = new Date().getFullYear();
    const [dep, schedE] = await Promise.all([
      api<AnyRecord>('GET', `/portfolio/properties/${p.id}/depreciation?years=30`),
      api<AnyRecord>('GET', `/portfolio/properties/${p.id}/schedule-e?year=${year}`),
    ]);

    const s = (dep.summary || {}) as AnyRecord;
    const rows = (dep.schedule as AnyRecord[]) || [];
    const hasBasis = Number(s.total_recoverable || 0) > 0;
    const method = String(s.method || 'straight_line_27_5').replace('straight_line_', '').replace('_', '.') + ' yr SL';

    // Split "3 — Rents received" (or "Line 3 — …" / "Net — …") into pill + label.
    const parseLine = (raw: string): { pill: string; label: string } => {
      const m = /^(?:Line\s*)?(\d+|Net)\s*[—\-–]\s*(.*)$/i.exec(raw);
      if (m) {
        const isNum = /^\d+$/.test(m[1]);
        return { pill: isNum ? `Line ${m[1]}` : m[1], label: m[2] || raw };
      }
      return { pill: '', label: raw };
    };
    const scheduleLines = (schedE.lines as AnyRecord[]) || [];
    const scheduleERows = scheduleLines.map((l) => {
      const parts = parseLine(String(l.line));
      const isNet = /net/i.test(parts.pill);
      const amt = Number(l.amount);
      const amtCls = isNet ? (amt >= 0 ? 'pos' : 'neg') : '';
      return `
        <tr class="${isNet ? 'sched-net-row' : ''}">
          <td class="sched-line-cell">
            ${parts.pill ? `<span class="sched-line-pill ${isNet ? 'net' : ''}">${escapeHtml(parts.pill)}</span>` : ''}
            <span class="sched-line-label">${escapeHtml(parts.label)}</span>
          </td>
          <td class="right"><strong class="${amtCls}">${fmtMoney(amt)}</strong></td>
        </tr>
      `;
    }).join('');

    // Estimated tax savings when the return shows a loss, at the
    // investor's saved bracket: the depreciation summary already carries
    // the bracket the backend resolved (_user_tax_bracket → investment_
    // criteria.tax_bracket_pct, else 32%); fall back to the same
    // resolution client-side so the two never disagree.
    const net = Number(schedE.net_rental_income);
    const bracketPct = resolveTaxBracketPct(s.tax_bracket_pct);
    const taxSavingsHighlight = net < 0
      ? `<div class="sched-savings-row">
          <span>Est. tax savings:</span>
          <strong class="pos">${fmtMoney(Math.abs(net) * bracketPct / 100)}</strong>
          <span class="sched-savings-rate">at ${bracketPct}% est. bracket</span>
        </div>`
      : '';

    const uncat = (schedE.uncategorized_expense_categories as string[]) || [];

    const depSummaryBlock = hasBasis ? `
      <section class="portfolio-detail-section">
        <h4>Depreciation summary</h4>
        <div class="portfolio-pnl-summary">
          <div><div class="portfolio-pnl-summary-label">Annual</div><div class="portfolio-pnl-summary-value">${fmtMoney(Number(s.annual_depreciation))}</div></div>
          <div><div class="portfolio-pnl-summary-label">Tax savings/yr</div><div class="portfolio-pnl-summary-value pos">${fmtMoney(Number(s.tax_savings_per_year))}</div></div>
          <div><div class="portfolio-pnl-summary-label">Method</div><div class="portfolio-pnl-summary-value" style="font-size:13px">${escapeHtml(method)}</div></div>
        </div>
        <div style="margin-top:10px;color:var(--text-muted);font-size:12px">
          Basis: ${fmtMoney(Number(s.total_recoverable))}${p.land_value ? ` · Land: ${fmtMoney(Number(p.land_value))}` : ''} · ${escapeHtml(String(s.tax_bracket_pct))}% bracket
        </div>
      </section>

      <section class="portfolio-detail-section">
        <h4>Schedule</h4>
        <table class="portfolio-pnl-table">
          <tr><th>Year</th><th class="right">Annual</th><th class="right">Accumulated</th><th class="right">Remaining</th><th class="right">Tax savings</th></tr>
          ${rows.map((r) => `
            <tr>
              <td>${escapeHtml(String(r.year))}</td>
              <td class="right">${fmtMoney(Number(r.annual_depreciation))}</td>
              <td class="right">${fmtMoney(Number(r.accumulated))}</td>
              <td class="right">${fmtMoney(Number(r.remaining_basis))}</td>
              <td class="right pos">${fmtMoney(Number(r.tax_savings))}</td>
            </tr>
          `).join('')}
        </table>
      </section>
    ` : `
      <section class="portfolio-detail-section">
        <h4>Depreciation</h4>
        <div class="portfolio-pnl-empty">Set a purchase price to generate the depreciation schedule.</div>
      </section>
    `;

    // Cost-segregation screening (non-blocking, fetched after the main render).
    const costSegSlot = '<div id="cs-slot"></div>';

    el.innerHTML = `
      ${depSummaryBlock}

      ${costSegSlot}

      <section class="portfolio-detail-section">
        <div class="sched-header-row">
          <h4 style="margin:0">Schedule E Summary Report · ${year}</h4>
          <button class="btn btn-secondary btn-sm" onclick="openScheduleEPrint('${escapeHtml(String(p.id))}', ${year})">
            <i data-feather="printer" width="13"></i><span>Print / PDF</span>
          </button>
        </div>
        <div class="sched-not-for-filing">FOR INFORMATIONAL PURPOSES ONLY — NOT FOR FILING</div>
        <table class="portfolio-pnl-table sched-table">
          ${scheduleERows}
        </table>
        ${taxSavingsHighlight}
        ${uncat.length ? `<div class="tax-uncat">Uncategorized: ${uncat.map(escapeHtml).join(', ')}. Map these in future reporting.</div>` : ''}
        <div class="tax-disclaimer">Estimate — not tax advice. Provide this report to your CPA for filing.</div>
      </section>

      <section class="portfolio-detail-section">
        <h4>Capital gains scenarios</h4>
        <div class="portfolio-form-row">
          <label>Proposed sale<input type="number" id="cg-price" min="1000" value="${Math.round(Number(p.current_value || p.purchase_price || 0))}" oninput="runCapitalGains()"></label>
          <label>Accum. depreciation<input type="number" id="cg-dep" min="0" value="${Math.round(Number(p.accumulated_depreciation || 0))}" oninput="runCapitalGains()"></label>
        </div>
        <div class="portfolio-form-row">
          <label>State rate %<input type="number" step="0.1" id="cg-state" min="0" max="20" value="5" oninput="runCapitalGains()"></label>
          <label>&nbsp;<div style="height:34px"></div></label>
        </div>
        <div id="cg-result"></div>
      </section>
    `;

    runCapitalGains();
    loadCostSegregation(p);
  } catch (err: any) {
    el.innerHTML = `<div class="portfolio-empty"><p>${escapeHtml(err.message || 'Failed to load tax data')}</p></div>`;
  }
}

export async function openScheduleEPrint(propertyId: string, year: number): Promise<void> {
  // Fetch printable HTML with the JWT on the Authorization header, then
  // open as a blob URL so we don't leak the token in query strings or
  // browser history. User prints to PDF from the browser's native print UI.
  try {
    const token = localStorage.getItem('swipehome_token') || '';
    const res = await fetch(
      `${(window as AnyRecord).API || 'http://localhost:3001/api'}/portfolio/properties/${encodeURIComponent(propertyId)}/schedule-e/print?year=${year}`,
      { headers: { Authorization: 'Bearer ' + token } },
    );
    if (!res.ok) throw new Error('Failed to generate report');
    const html = await res.text();
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (!win) {
      // Pop-up blocked — offer a download fallback.
      const a = document.createElement('a');
      a.href = url;
      a.download = `schedule-e-${year}.html`;
      a.click();
    }
    // Release the blob after a delay so the new tab has time to load.
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  } catch (err: any) {
    showToast(err.message || 'Could not open report', 'error');
  }
}

async function loadCostSegregation(p: AnyRecord): Promise<void> {
  const slot = document.getElementById('cs-slot');
  if (!slot) return;
  try {
    const r = await api<AnyRecord>('GET', `/portfolio/properties/${p.id}/cost-segregation`);
    if (!r.basis || Number(r.basis) <= 0) {
      slot.innerHTML = '';
      return;
    }
    const worth = !!r.worth_studying;
    const tone = worth ? 'pos' : 'warn';
    slot.innerHTML = `
      <section class="portfolio-detail-section cs-section cs-${tone}">
        <h4>Cost segregation screening</h4>
        <div class="cs-hero">
          <div class="cs-hero-value ${tone}">${fmtMoney(Number(r.year_1_tax_savings))}</div>
          <div class="cs-hero-label">Year-1 tax savings estimate</div>
        </div>
        <div class="cs-meta">
          <div><span>Study fee</span><strong>${fmtMoney(Number(r.study_fee_range.low))}–${fmtMoney(Number(r.study_fee_range.high))}</strong></div>
          <div><span>Baseline yr-1</span><strong>${fmtMoney(Number(r.baseline_year_1_deduction))}</strong></div>
          <div><span>Accelerated yr-1</span><strong class="pos">${fmtMoney(Number(r.accelerated_year_1_deduction))}</strong></div>
          <div><span>Reclassified</span><strong>${Number(r.reclassified_pct).toFixed(1)}%</strong></div>
        </div>
        <div class="cs-recommendation cs-rec-${tone}">${escapeHtml(String(r.recommendation))}</div>
        <table class="portfolio-pnl-table" style="margin-top:10px">
          <tr><th>Asset class</th><th class="right">Reclassified</th><th class="right">Yr-1 deduction</th></tr>
          ${(r.class_breakdown as AnyRecord[]).map((c) => `
            <tr>
              <td>${escapeHtml(String(c.class))}</td>
              <td class="right">${fmtMoney(Number(c.reclassified_basis))}</td>
              <td class="right"><strong class="pos">${fmtMoney(Number(c.year_1_deduction))}</strong></td>
            </tr>
          `).join('')}
        </table>
        ${complianceFooter(DISCLAIMER.costSeg)}
      </section>
    `;
  } catch {
    /* silent — cost-seg is additive */
  }
}

let capitalGainsDebounce: ReturnType<typeof setTimeout> | null = null;

export function runCapitalGains(): void {
  if (capitalGainsDebounce) clearTimeout(capitalGainsDebounce);
  capitalGainsDebounce = setTimeout(fetchCapitalGains, 250);
}

async function fetchCapitalGains(): Promise<void> {
  const el = document.getElementById('cg-result');
  if (!el || !selectedPortfolioId) return;
  const getN = (id: string) => Number((document.getElementById(id) as HTMLInputElement | null)?.value || 0);
  const price = getN('cg-price');
  const dep = getN('cg-dep');
  const state = getN('cg-state');
  if (!price || price < 1000) {
    el.innerHTML = '<div class="portfolio-pnl-empty">Enter a sale price above $1,000 to compute scenarios.</div>';
    return;
  }
  try {
    const r = await api<AnyRecord>('POST', `/portfolio/properties/${selectedPortfolioId}/capital-gains`, {
      sale_price: price,
      accumulated_depreciation: dep,
      state_rate_pct: state,
      niit_applies: true,
    });
    const gain = Number(r.gain || 0);
    const sn = (r.sell_now || {}) as AnyRecord;
    const ex = (r.exchange_1031 || {}) as AnyRecord;
    const bd = (sn.breakdown || {}) as AnyRecord;

    el.innerHTML = `
      <div class="cg-gain">Total gain <strong>${fmtMoney(gain)}</strong></div>
      <div class="cg-scenarios">
        <div class="cg-scenario cg-sell">
          <div class="cg-scenario-head">Sell now</div>
          <div class="cg-scenario-value">${fmtMoney(Number(sn.net_proceeds))}</div>
          <div class="cg-scenario-sub">${fmtMoney(Number(sn.total_tax))} tax</div>
        </div>
        <div class="cg-scenario cg-hold">
          <div class="cg-scenario-head">Hold</div>
          <div class="cg-scenario-value">—</div>
          <div class="cg-scenario-sub">No transaction</div>
        </div>
        <div class="cg-scenario cg-exchange">
          <div class="cg-scenario-head">1031 exchange</div>
          <div class="cg-scenario-value">${fmtMoney(Number(ex.net_proceeds))}</div>
          <div class="cg-scenario-sub">${fmtMoney(Number(ex.tax_deferred))} deferred</div>
        </div>
      </div>
      <div class="cg-breakdown">
        <div><span>Federal cap gains</span><strong>${fmtMoney(Number(bd.federal_capital_gains))}</strong></div>
        <div><span>Depreciation recapture (25%)</span><strong>${fmtMoney(Number(bd.depreciation_recapture))}</strong></div>
        <div><span>NIIT (3.8%)</span><strong>${fmtMoney(Number(bd.niit))}</strong></div>
        <div><span>State tax</span><strong>${fmtMoney(Number(bd.state))}</strong></div>
        <div><span>Selling costs (8%)</span><strong>${fmtMoney(Number(bd.selling_costs))}</strong></div>
      </div>
      ${complianceFooter(DISCLAIMER.capitalGains)}
    `;
  } catch (err: any) {
    el.innerHTML = `<div class="portfolio-pnl-empty">${escapeHtml(err.message || 'Calc failed')}</div>`;
  }
}

// ═══════════════════════════════════════════════════════════════
// CAPEX TAB — reserve lifecycle tracker (Phase 2E)
// ═══════════════════════════════════════════════════════════════

const CAPEX_LIFE_DEFAULTS: Record<string, number> = {
  roof: 25, hvac: 18, water_heater: 12, appliances: 12, flooring: 20,
  exterior_paint: 10, plumbing: 50, electrical: 40, windows: 25, foundation: 75,
};
const CAPEX_COMPONENTS = Object.keys(CAPEX_LIFE_DEFAULTS);

async function renderCapexTab(el: HTMLElement, p: AnyRecord): Promise<void> {
  el.innerHTML = `<div style="color:var(--text-secondary);font-size:13px;padding:12px 0">Loading reserves...</div>`;
  try {
    const data = await api<AnyRecord>('GET', `/portfolio/properties/${p.id}/capex`);
    const items = (data.items as AnyRecord[]) || [];

    const totalMonthly = items.reduce((sum, r) => sum + Number(r.monthly_reserve_amount || 0), 0);
    const rows = items.map(renderCapexRow).join('');

    const header = `
      <section class="portfolio-detail-section">
        <h4>Reserve summary</h4>
        <div class="portfolio-pnl-summary">
          <div><div class="portfolio-pnl-summary-label">Monthly reserve</div><div class="portfolio-pnl-summary-value">${fmtMoney(totalMonthly)}</div></div>
          <div><div class="portfolio-pnl-summary-label">Components</div><div class="portfolio-pnl-summary-value">${items.length}</div></div>
          <div><div class="portfolio-pnl-summary-label">At risk</div><div class="portfolio-pnl-summary-value neg">${items.filter((r) => remainingLifePct(r) < 25).length}</div></div>
        </div>
      </section>
    `;

    const addBtn = `
      <button class="btn btn-secondary btn-sm" onclick="openCapexModal('${escapeHtml(String(p.id))}')" style="margin:0 0 12px">
        <i data-feather="plus" width="13"></i><span>Add component</span>
      </button>
    `;

    el.innerHTML = header + addBtn + (items.length
      ? `<div class="capex-list">${rows}</div>`
      : `<div class="portfolio-empty"><p>No reserves tracked yet. Add the major components (roof, HVAC, etc.) to build a replacement schedule.</p></div>`);
    if (window.feather) window.feather.replace();
  } catch (err: any) {
    el.innerHTML = `<div class="portfolio-empty"><p>${escapeHtml(err.message || 'Failed to load reserves')}</p></div>`;
  }
}

function remainingLifePct(r: AnyRecord): number {
  const life = Number(r.estimated_lifespan_years || 0);
  const age = Number(r.age_years || 0);
  if (life <= 0) return 100;
  return Math.max(0, Math.min(100, ((life - age) / life) * 100));
}

function lifeToneFor(pct: number): string {
  if (pct < 25) return 'neg';
  if (pct < 50) return 'warn';
  return 'pos';
}

function prettyComponent(c: string): string {
  return c.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function renderCapexRow(r: AnyRecord): string {
  const pct = remainingLifePct(r);
  const tone = lifeToneFor(pct);
  const life = Number(r.estimated_lifespan_years || 0);
  const age = Number(r.age_years || 0);
  const remaining = Math.max(0, life - age);
  const cost = Number(r.estimated_replacement_cost || 0);
  const reserve = Number(r.monthly_reserve_amount || 0);
  return `
    <div class="capex-row">
      <div class="capex-row-head">
        <div class="capex-component">${escapeHtml(prettyComponent(String(r.component || '')))}</div>
        <button class="portfolio-tx-delete" onclick="deleteCapex('${escapeHtml(String(r.id))}')" aria-label="Delete">
          <i data-feather="x" width="12"></i>
        </button>
      </div>
      <div class="capex-meta">
        <span>Age <strong>${age}y</strong></span>
        <span>Life <strong>${life}y</strong></span>
        <span>Cost <strong>${fmtMoneyCompact(cost)}</strong></span>
        <span>Reserve <strong>${fmtMoney(reserve)}/mo</strong></span>
      </div>
      <div class="capex-life-bar">
        <div class="capex-life-fill ${tone}" style="width:${pct.toFixed(1)}%"></div>
      </div>
      <div class="capex-life-label">
        ${remaining}y remaining · Condition: <strong>${escapeHtml(String(r.condition || 'good'))}</strong>
      </div>
    </div>
  `;
}

export function openCapexModal(propertyId: string): void {
  const openModal = (window as AnyRecord as {
    openModal: (t: string, body: string, buttons: AnyRecord[]) => void;
  }).openModal;
  const closeModal = (window as AnyRecord as { closeModal: () => void }).closeModal;
  const body = `
    <div class="portfolio-form">
      <label>Component
        <select id="cx-component" onchange="onCapexComponentChange()">
          ${CAPEX_COMPONENTS.map((c) => `<option value="${c}">${prettyComponent(c)}</option>`).join('')}
          <option value="other">Other</option>
        </select>
      </label>
      <div class="portfolio-form-row">
        <label>Age (yrs)<input type="number" id="cx-age" min="0" max="200" value="0"></label>
        <label>Lifespan (yrs)<input type="number" id="cx-life" min="1" max="200" value="25"></label>
      </div>
      <div class="portfolio-form-row">
        <label>Replacement cost<input type="number" id="cx-cost" min="0" placeholder="$"></label>
        <label>Monthly reserve<input type="number" id="cx-reserve" min="0" placeholder="$"></label>
      </div>
      <label>Condition
        <select id="cx-condition">
          <option value="excellent">Excellent</option>
          <option value="good" selected>Good</option>
          <option value="fair">Fair</option>
          <option value="poor">Poor</option>
          <option value="critical">Critical</option>
        </select>
      </label>
      <label>Last replaced
        <input type="date" id="cx-last">
      </label>
      <label>Notes<textarea id="cx-notes" rows="2"></textarea></label>
    </div>
  `;
  openModal('Add CapEx component', body, [
    { label: 'Cancel', style: 'secondary', onclick: closeModal },
    { label: 'Add', style: 'primary', onclick: () => submitCapex(propertyId) },
  ]);
  setTimeout(() => onCapexComponentChange(), 50);
}

export function onCapexComponentChange(): void {
  const sel = document.getElementById('cx-component') as HTMLSelectElement | null;
  const lifeInput = document.getElementById('cx-life') as HTMLInputElement | null;
  if (!sel || !lifeInput) return;
  const def = CAPEX_LIFE_DEFAULTS[sel.value];
  if (def) lifeInput.value = String(def);
}

async function submitCapex(propertyId: string): Promise<void> {
  const getV = (id: string) => (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value?.trim() || '';
  const getN = (id: string) => {
    const v = getV(id);
    return v ? Number(v) : null;
  };
  const payload: AnyRecord = {
    component: getV('cx-component'),
    age_years: getN('cx-age'),
    estimated_lifespan_years: getN('cx-life'),
    estimated_replacement_cost: getN('cx-cost'),
    monthly_reserve_amount: getN('cx-reserve'),
    condition: getV('cx-condition') || 'good',
    last_replaced_date: getV('cx-last') || null,
    notes: getV('cx-notes') || null,
  };
  // Auto-compute reserve if blank: cost / (remaining_life * 12).
  const life = payload.estimated_lifespan_years || 0;
  const age = payload.age_years || 0;
  const cost = payload.estimated_replacement_cost || 0;
  const remaining = Math.max(1, life - age);
  if (cost > 0 && !payload.monthly_reserve_amount) {
    payload.monthly_reserve_amount = Number((cost / (remaining * 12)).toFixed(2));
  }
  Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);

  try {
    await api('POST', `/portfolio/properties/${propertyId}/capex`, payload);
    (window as AnyRecord).closeModal?.();
    showToast('Component added', 'success');
    renderDetailTab();
  } catch (err: any) {
    showToast(err.message || 'Could not add', 'error');
  }
}

export async function deleteCapex(capexId: string): Promise<void> {
  if (!confirm('Remove this component reserve?')) return;
  try {
    await api('DELETE', `/portfolio/capex/${capexId}`);
    showToast('Removed', 'success');
    renderDetailTab();
  } catch (err: any) {
    showToast(err.message || 'Could not remove', 'error');
  }
}

// ═══════════════════════════════════════════════════════════════
// REPS HOURS WIDGET (Phase 2C)
// ═══════════════════════════════════════════════════════════════

export async function loadReps(): Promise<void> {
  const el = document.getElementById('portfolio-reps');
  if (!el) return;
  try {
    const data = await api<AnyRecord>('GET', '/portfolio/reps/summary');
    const total = Number(data.total_hours || 0);
    const target = Number(data.target_hours || 750);
    if (!(Number(data.entries_count ?? 0) > 0 || total > 0)) {
      el.innerHTML = sectionEmpty(
        'clock',
        `No hours logged yet — log hours toward your ${new Date().getFullYear()} real estate professional status.`,
      );
      if (window.feather) window.feather.replace();
      return;
    }
    const pct = Number(data.progress_pct || 0);
    const qualifies = !!data.qualifies;
    const onPace = !!data.on_pace;
    const projected = Number(data.projected_year_total || 0);
    const perWeek = Number(data.hours_per_week_needed || 0);
    const barTone = qualifies ? 'pos' : onPace ? 'warn' : 'neg';

    const statusLabel = qualifies
      ? 'Qualified ✓'
      : onPace
        ? `On pace — projecting ${projected.toFixed(0)}h`
        : `${perWeek.toFixed(1)}h/wk needed`;

    el.innerHTML = `
      <div class="reps-card">
        <div class="reps-top">
          <div class="reps-hours">
            <div class="reps-hours-value">${total.toFixed(1)}<span>/${target}h</span></div>
            <div class="reps-hours-label">Year-to-date</div>
          </div>
          <div class="reps-status reps-status-${barTone}">${escapeHtml(statusLabel)}</div>
        </div>
        <div class="reps-bar">
          <div class="reps-bar-fill reps-bar-${barTone}" style="width:${Math.min(100, pct).toFixed(1)}%"></div>
        </div>
        <div class="reps-sub">${pct.toFixed(1)}% of 750-hr threshold</div>
        ${complianceFooter(DISCLAIMER.reps)}
      </div>
    `;
  } catch (err: any) {
    sectionUnavailable('portfolio-reps', 'REPS hours', 'loadReps()', err);
  }
}

export function openRepsModal(): void {
  const openModal = (window as AnyRecord as {
    openModal: (t: string, body: string, buttons: AnyRecord[]) => void;
  }).openModal;
  const closeModal = (window as AnyRecord as { closeModal: () => void }).closeModal;
  const today = new Date().toISOString().slice(0, 10);
  const propOptions = ownedPropsCache
    .map((p) => `<option value="${escapeHtml(String(p.id))}">${escapeHtml(String(p.address || ''))}</option>`)
    .join('');
  const body = `
    <div class="portfolio-form">
      <div class="portfolio-form-row">
        <label>Date<input type="date" id="reps-date" value="${today}"></label>
        <label>Hours<input type="number" step="0.5" id="reps-hours" min="0.5" max="24" placeholder="4"></label>
      </div>
      <label>Activity type
        <select id="reps-type">
          <option value="property_management">Property management</option>
          <option value="tenant_communication">Tenant communication</option>
          <option value="maintenance_oversight">Maintenance oversight</option>
          <option value="bookkeeping">Bookkeeping</option>
          <option value="market_research">Market research</option>
          <option value="deal_analysis">Deal analysis</option>
        </select>
      </label>
      <label>Property (optional)
        <select id="reps-prop">
          <option value="">— Unassigned —</option>
          ${propOptions}
        </select>
      </label>
      <label>Description<input type="text" id="reps-desc" placeholder="Optional"></label>
      <div id="reps-history" style="margin-top:12px"></div>
    </div>
  `;
  openModal('Log REPS hours', body, [
    { label: 'Close', style: 'secondary', onclick: closeModal },
    { label: 'Save', style: 'primary', onclick: submitReps },
  ]);
  setTimeout(loadRepsHistoryIntoModal, 80);
}

async function loadRepsHistoryIntoModal(): Promise<void> {
  const el = document.getElementById('reps-history');
  if (!el) return;
  try {
    const summary = await api<AnyRecord>('GET', '/portfolio/reps/summary');
    const monthly = (summary.monthly as AnyRecord[]) || [];
    if (monthly.length === 0) {
      el.innerHTML = `<div style="color:var(--text-muted);font-size:12px">No hours logged yet.</div>`;
      return;
    }
    el.innerHTML = `
      <div style="font-size:11px;font-weight:700;color:var(--primary);text-transform:uppercase;letter-spacing:0.05em;margin-bottom:6px">Monthly breakdown</div>
      <table class="portfolio-pnl-table">
        ${monthly.map((m) => `<tr><td>${escapeHtml(String(m.label))}</td><td class="right"><strong>${Number(m.hours).toFixed(1)}h</strong></td></tr>`).join('')}
      </table>
    `;
  } catch {
    /* silent */
  }
}

async function submitReps(): Promise<void> {
  const date = (document.getElementById('reps-date') as HTMLInputElement | null)?.value;
  const hours = (document.getElementById('reps-hours') as HTMLInputElement | null)?.value;
  const type = (document.getElementById('reps-type') as HTMLSelectElement | null)?.value;
  const prop = (document.getElementById('reps-prop') as HTMLSelectElement | null)?.value;
  const desc = (document.getElementById('reps-desc') as HTMLInputElement | null)?.value?.trim();
  if (!date || !hours || Number(hours) <= 0) {
    showToast('Date and hours required', 'error');
    return;
  }
  try {
    await api('POST', '/portfolio/reps', {
      activity_date: date,
      hours: Number(hours),
      activity_type: type || null,
      property_id: prop || null,
      description: desc || null,
    });
    showToast('Logged', 'success');
    // Re-render history inside the still-open modal; refresh dashboard widget.
    loadRepsHistoryIntoModal();
    loadReps();
    // Clear hours field for next entry.
    const h = document.getElementById('reps-hours') as HTMLInputElement | null;
    if (h) h.value = '';
  } catch (err: any) {
    showToast(err.message || 'Could not log', 'error');
  }
}

// ═══════════════════════════════════════════════════════════════
// ANALYTICS SCREEN (Phase 3A/3B/3C)
// ═══════════════════════════════════════════════════════════════

declare global {
  interface Window {
    Chart?: any;
  }
}

let activeCharts: any[] = [];
let stressDebounce: ReturnType<typeof setTimeout> | null = null;

export function renderPortfolioAnalytics(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'fade-in screen-padded portfolio-screen';
  div.innerHTML = `
    <div class="portfolio-detail-header">
      <button class="portfolio-back" onclick="navigate('portfolio')">
        <i data-feather="arrow-left" width="18"></i>
      </button>
      <div class="portfolio-detail-heading">
        <div class="portfolio-detail-addr">Analytics</div>
        <div class="portfolio-detail-sub">Portfolio-level insights</div>
      </div>
    </div>

    <section class="portfolio-detail-section">
      <h4>Monthly cash flow · 12 months</h4>
      <div class="portfolio-chart-wrap"><canvas id="pa-cashflow"></canvas></div>
    </section>

    <section class="portfolio-detail-section">
      <h4>Income vs expenses · TTM</h4>
      <div class="portfolio-chart-wrap"><canvas id="pa-donut"></canvas></div>
    </section>

    <section class="portfolio-detail-section">
      <h4>Property comparison</h4>
      <div class="portfolio-analytics-compare-toggle">
        <button class="portfolio-tab active" onclick="switchCompareMetric('cap_rate')" id="pa-cmp-cap">Cap rate</button>
        <button class="portfolio-tab" onclick="switchCompareMetric('cash_on_cash')" id="pa-cmp-coc">CoC</button>
        <button class="portfolio-tab" onclick="switchCompareMetric('monthly_cash_flow')" id="pa-cmp-cf">Cash flow</button>
      </div>
      <div class="portfolio-chart-wrap"><canvas id="pa-compare"></canvas></div>
    </section>

    <section class="portfolio-detail-section">
      <h4>Benchmark vs market</h4>
      <div id="pa-benchmark">
        <div class="skeleton-row"><div class="skeleton" style="height:40px;border-radius:8px"></div></div>
      </div>
    </section>

    <section class="portfolio-detail-section">
      <h4>Geographic diversification</h4>
      <div id="pa-diversification">
        <div class="skeleton-row"><div class="skeleton" style="height:40px;border-radius:8px"></div></div>
      </div>
    </section>

    <section class="portfolio-detail-section">
      <h4>FIRE trajectory</h4>
      <div class="portfolio-form-row">
        <label>Target monthly expenses<input type="number" id="pa-fire-target" min="0" value="6000" oninput="onFireInput()"></label>
        <label>Rent growth %<input type="number" step="0.5" id="pa-fire-growth" min="-5" max="20" value="3" oninput="onFireInput()"></label>
      </div>
      <div id="pa-fire"></div>
    </section>

    <section class="portfolio-detail-section">
      <h4>Predictive maintenance</h4>
      <div id="pa-maintenance">
        <div class="skeleton-row"><div class="skeleton" style="height:40px;border-radius:8px"></div></div>
      </div>
    </section>

    <section class="portfolio-detail-section">
      <h4>Stress test</h4>
      <div class="portfolio-stress-form">
        <label>Rate shock (pp)
          <input type="range" id="pa-rate" min="-2" max="5" step="0.25" value="0" oninput="onStressSlider()">
          <span id="pa-rate-val">0.00</span>
        </label>
        <label>Vacancy shock (pp)
          <input type="range" id="pa-vac" min="-10" max="30" step="1" value="0" oninput="onStressSlider()">
          <span id="pa-vac-val">0</span>
        </label>
        <label>Rent change (%)
          <input type="range" id="pa-rent" min="-30" max="20" step="1" value="0" oninput="onStressSlider()">
          <span id="pa-rent-val">0</span>
        </label>
        <label>Value change (%)
          <input type="range" id="pa-val" min="-40" max="30" step="1" value="0" oninput="onStressSlider()">
          <span id="pa-val-val">0</span>
        </label>
      </div>
      <div id="pa-stress-result"></div>
    </section>
  `;
  setTimeout(() => {
    loadPortfolioAnalytics();
    if (window.feather) window.feather.replace();
  }, 50);
  return div;
}

let lastComparisonData: AnyRecord[] = [];
let lastCompareMetric: 'cap_rate' | 'cash_on_cash' | 'monthly_cash_flow' = 'cap_rate';

async function loadPortfolioAnalytics(): Promise<void> {
  destroyCharts();
  try {
    const [charts, benchmark, diversification, maintenance] = await Promise.all([
      api<AnyRecord>('GET', '/portfolio/charts?months=12'),
      api<AnyRecord>('GET', '/portfolio/benchmark'),
      api<AnyRecord>('GET', '/portfolio/diversification'),
      api<AnyRecord>('GET', '/portfolio/predictive-maintenance'),
    ]);
    renderCashFlowChart(charts.cash_flow_trend || []);
    renderDonutChart(charts.income_vs_expenses || {});
    lastComparisonData = (charts.property_comparison as AnyRecord[]) || [];
    renderComparisonChart(lastComparisonData, lastCompareMetric);
    renderBenchmark(benchmark);
    renderDiversification(diversification);
    renderMaintenance(maintenance);
    runFireProjection();
    runStressTest();
  } catch (err: any) {
    showToast(err.message || 'Failed to load analytics', 'error');
  }
}

function renderDiversification(d: AnyRecord): void {
  const el = document.getElementById('pa-diversification');
  if (!el) return;
  const states = (d.by_state as AnyRecord[]) || [];
  if (states.length === 0) {
    el.innerHTML = `<div class="portfolio-pnl-empty">Add properties with state info to see diversification.</div>`;
    return;
  }
  const verdict = String(d.verdict || 'diversified');
  const verdictLabel: Record<string, string> = {
    diversified: 'Diversified',
    moderate: 'Moderate concentration',
    concentrated: 'Concentrated',
    high_risk: 'High concentration risk',
  };
  const alerts = (d.alerts as AnyRecord[]) || [];

  el.innerHTML = `
    <div class="div-verdict div-verdict-${verdict}">
      <span class="div-verdict-label">${escapeHtml(verdictLabel[verdict] || verdict)}</span>
      <span class="div-verdict-sub">HHI ${Math.round(Number(d.state_hhi || 0))}</span>
    </div>
    ${alerts.length ? `<div class="div-alerts">${alerts.map((a) => `
      <div class="div-alert div-alert-${escapeHtml(String(a.severity))}">
        <i data-feather="alert-triangle" width="13"></i>
        <span>${escapeHtml(String(a.message))}</span>
      </div>
    `).join('')}</div>` : ''}
    <div class="div-bar-list">
      ${states.map((s) => `
        <div class="div-bar-row">
          <div class="div-bar-label"><strong>${escapeHtml(String(s.state))}</strong> <span>${fmtMoneyCompact(Number(s.value))}</span></div>
          <div class="div-bar-track"><div class="div-bar-fill" style="width:${Number(s.pct).toFixed(1)}%"></div></div>
          <div class="div-bar-pct">${Number(s.pct).toFixed(1)}%</div>
        </div>
      `).join('')}
    </div>
    ${(d.by_city as AnyRecord[] || []).length > 1 ? `
      <div style="margin-top:12px;font-size:11px;font-weight:700;color:var(--primary);text-transform:uppercase;letter-spacing:0.05em;margin-bottom:6px">By metro</div>
      <table class="portfolio-pnl-table">
        ${(d.by_city as AnyRecord[]).map((c) => `
          <tr><td>${escapeHtml(String(c.market))}</td><td class="right"><strong>${fmtMoneyCompact(Number(c.value))}</strong></td><td class="right">${Number(c.pct).toFixed(1)}%</td></tr>
        `).join('')}
      </table>
    ` : ''}
  `;
  if (window.feather) window.feather.replace();
}

function renderMaintenance(m: AnyRecord): void {
  const el = document.getElementById('pa-maintenance');
  if (!el) return;
  const total = Number(m.total_components || 0);
  if (total === 0) {
    el.innerHTML = `<div class="portfolio-pnl-empty">Track capex components on a property's CapEx tab to unlock risk alerts.</div>`;
    return;
  }
  const imminent = Number(m.imminent_count || 0);
  const soon = Number(m.soon_count || 0);
  const gap = Number(m.total_reserve_gap || 0);
  const top = (m.top_risks as AnyRecord[]) || [];

  const headerTone = imminent > 0 ? 'neg' : soon > 0 ? 'warn' : 'pos';
  const headerMsg = imminent > 0
    ? `${imminent} component${imminent === 1 ? '' : 's'} flagged as imminent`
    : soon > 0
      ? `${soon} component${soon === 1 ? '' : 's'} trending toward replacement`
      : 'All tracked components healthy';

  el.innerHTML = `
    <div class="maint-header maint-${headerTone}">
      <span>${escapeHtml(headerMsg)}</span>
      ${gap > 0 ? `<span class="maint-gap">Reserve gap ${fmtMoneyCompact(gap)}</span>` : ''}
    </div>
    ${top.map((t) => {
      const urgency = String(t.urgency || 'ok');
      const life = Number(t.life_remaining_pct || 0);
      return `
        <div class="maint-row maint-row-${urgency}">
          <div class="maint-row-head">
            <div class="maint-row-component">${escapeHtml(prettyComponent(String(t.component || '')))}</div>
            <div class="maint-row-score">${t.risk_score}</div>
          </div>
          <div class="maint-row-sub">${escapeHtml(String(t.property_address || ''))} · ${t.remaining_years}y remaining · ${escapeHtml(String(t.condition || 'good'))}</div>
          <div class="maint-row-bar"><div class="maint-row-fill" style="width:${life.toFixed(1)}%"></div></div>
        </div>
      `;
    }).join('')}
  `;
}

let fireDebounce: ReturnType<typeof setTimeout> | null = null;
export function onFireInput(): void {
  if (fireDebounce) clearTimeout(fireDebounce);
  fireDebounce = setTimeout(runFireProjection, 260);
}

async function runFireProjection(): Promise<void> {
  const el = document.getElementById('pa-fire');
  if (!el) return;
  const target = Number((document.getElementById('pa-fire-target') as HTMLInputElement | null)?.value || 0);
  const growth = Number((document.getElementById('pa-fire-growth') as HTMLInputElement | null)?.value || 3);
  if (target <= 0) {
    el.innerHTML = '<div class="portfolio-pnl-empty">Enter your target monthly living expenses above.</div>';
    return;
  }
  try {
    const r = await api<AnyRecord>('POST', '/portfolio/fire', {
      target_monthly_expenses: target,
      cash_flow_growth_pct: growth,
    });
    const coverage = Number(r.current_coverage_pct || 0);
    const years = r.years_to_fi;
    const already = !!r.already_fi;
    const pct = Math.max(0, Math.min(100, coverage));
    const tone = already ? 'pos' : coverage >= 50 ? 'warn' : 'neg';

    const headline = already
      ? 'Already FI — your rentals cover target expenses today.'
      : years != null
        ? `On track to cover target expenses in <strong>${Number(years).toFixed(0)}y</strong> at ${growth}% cash-flow growth.`
        : `At ${growth}% growth, you won't reach FI within 25 years. Increase cash flow or reduce target.`;

    el.innerHTML = `
      <div class="fire-headline fire-${tone}">${headline}</div>
      <div class="fire-meter">
        <div class="fire-meter-fill fire-fill-${tone}" style="width:${pct.toFixed(1)}%"></div>
      </div>
      <div class="fire-sub">
        <span>${coverage.toFixed(1)}% of ${fmtMoney(Number(r.target_annual_expenses || 0))}/yr covered</span>
        <span>CF ${fmtMoney(Number(r.annual_cash_flow || 0))}/yr</span>
      </div>
    `;
  } catch (err: any) {
    el.innerHTML = `<div class="portfolio-pnl-empty">${escapeHtml(err.message || 'FIRE calc failed')}</div>`;
  }
}

function destroyCharts(): void {
  activeCharts.forEach((c) => { try { c.destroy(); } catch {} });
  activeCharts = [];
}

function chartDefaults(): AnyRecord {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: '#94a3b8', font: { size: 11 } } },
      tooltip: { backgroundColor: '#1e1b3a', titleColor: '#fff', bodyColor: '#cbd5e1', padding: 10 },
    },
    scales: {
      x: { ticks: { color: '#64748b', font: { size: 10 } }, grid: { color: 'rgba(99,102,241,0.08)' } },
      y: { ticks: { color: '#64748b', font: { size: 10 } }, grid: { color: 'rgba(99,102,241,0.08)' } },
    },
  };
}

function renderCashFlowChart(trend: AnyRecord[]): void {
  const canvas = document.getElementById('pa-cashflow') as HTMLCanvasElement | null;
  if (!canvas || !window.Chart) return;
  const labels = trend.map((t) => String(t.month));
  const data = trend.map((t) => Number(t.net || 0));
  const colors = data.map((v) => v >= 0 ? '#22c55e' : '#ef4444');
  const chart = new window.Chart(canvas, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: 'Net cash flow',
        data,
        backgroundColor: colors,
        borderRadius: 4,
      }],
    },
    options: chartDefaults(),
  });
  activeCharts.push(chart);
}

function renderDonutChart(ivx: AnyRecord): void {
  const canvas = document.getElementById('pa-donut') as HTMLCanvasElement | null;
  if (!canvas || !window.Chart) return;
  const exp = (ivx.expense_by_category || {}) as Record<string, number>;
  const labels = Object.keys(exp);
  const data = Object.values(exp);
  if (labels.length === 0) {
    canvas.parentElement!.innerHTML = `<div style="color:var(--text-muted);font-size:12px;padding:12px 0">No expense history yet.</div>`;
    return;
  }
  const palette = ['#6366f1', '#a78bfa', '#22d3ee', '#f59e0b', '#ef4444', '#22c55e', '#f472b6', '#60a5fa', '#facc15', '#14b8a6'];
  const opts = chartDefaults();
  delete opts.scales;
  const chart = new window.Chart(canvas, {
    type: 'doughnut',
    data: {
      labels,
      datasets: [{
        data,
        backgroundColor: labels.map((_, i) => palette[i % palette.length]),
        borderWidth: 0,
      }],
    },
    options: opts,
  });
  activeCharts.push(chart);
}

function renderComparisonChart(rows: AnyRecord[], metric: 'cap_rate' | 'cash_on_cash' | 'monthly_cash_flow'): void {
  const canvas = document.getElementById('pa-compare') as HTMLCanvasElement | null;
  if (!canvas || !window.Chart) return;
  // Destroy any prior comparison chart so we can swap metrics.
  const prior = activeCharts.find((c) => c?.canvas === canvas);
  if (prior) {
    try { prior.destroy(); } catch {}
    activeCharts = activeCharts.filter((c) => c !== prior);
  }
  const labels = rows.map((r) => String(r.address).split(',')[0].slice(0, 22));
  const data = rows.map((r) => Number(r[metric] || 0));
  const colors = data.map((v) => v >= 0 ? '#6366f1' : '#ef4444');
  const chart = new window.Chart(canvas, {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        label: metric.replace(/_/g, ' '),
        data,
        backgroundColor: colors,
        borderRadius: 4,
      }],
    },
    options: { ...chartDefaults(), indexAxis: 'y' },
  });
  activeCharts.push(chart);
}

export function switchCompareMetric(metric: string): void {
  if (!['cap_rate', 'cash_on_cash', 'monthly_cash_flow'].includes(metric)) return;
  lastCompareMetric = metric as typeof lastCompareMetric;
  document.querySelectorAll('.portfolio-analytics-compare-toggle .portfolio-tab').forEach((btn) => {
    btn.classList.remove('active');
  });
  document.getElementById(
    metric === 'cap_rate' ? 'pa-cmp-cap' : metric === 'cash_on_cash' ? 'pa-cmp-coc' : 'pa-cmp-cf',
  )?.classList.add('active');
  renderComparisonChart(lastComparisonData, lastCompareMetric);
}

function renderBenchmark(b: AnyRecord): void {
  const el = document.getElementById('pa-benchmark');
  if (!el) return;
  const you = Number(b.portfolio_annualized_return_pct || 0);
  const sp = Number(b.sp500_annualized_pct || 0);
  const reit = Number(b.vgslx_annualized_pct || 0);
  const years = Number(b.years_considered || 1);
  const vsSp = you - sp;
  const vsReit = you - reit;
  // The index series ends at the last published calendar year; the
  // portfolio figure is a forward 12-month estimate — say so rather than
  // implying a same-year comparison.
  const indexThrough = b.index_data_through != null ? String(b.index_data_through) : '';
  const comparisonNote = b.comparison_note ? String(b.comparison_note) : '';

  el.innerHTML = `
    <div class="portfolio-benchmark">
      <div class="portfolio-benchmark-primary">
        <div class="portfolio-benchmark-value">${you.toFixed(2)}%</div>
        <div class="portfolio-benchmark-label">Your portfolio (${years}-yr annualized)</div>
      </div>
      <div class="portfolio-benchmark-row">
        <div class="portfolio-benchmark-peer">
          <span>S&P 500</span>
          <strong>${sp.toFixed(2)}%</strong>
        </div>
        <div class="portfolio-benchmark-delta ${vsSp >= 0 ? 'pos' : 'neg'}">
          ${vsSp >= 0 ? '+' : ''}${vsSp.toFixed(2)}pp
        </div>
      </div>
      <div class="portfolio-benchmark-row">
        <div class="portfolio-benchmark-peer">
          <span>Vanguard REIT (VGSLX)</span>
          <strong>${reit.toFixed(2)}%</strong>
        </div>
        <div class="portfolio-benchmark-delta ${vsReit >= 0 ? 'pos' : 'neg'}">
          ${vsReit >= 0 ? '+' : ''}${vsReit.toFixed(2)}pp
        </div>
      </div>
      <div class="portfolio-benchmark-note">
        Estimate uses 3.5% assumed appreciation + principal paydown + trailing cash flow.
      </div>
      ${indexThrough ? `<div class="portfolio-benchmark-note">Index data through ${escapeHtml(indexThrough)}.</div>` : ''}
      ${comparisonNote ? `<div class="portfolio-benchmark-note portfolio-benchmark-caption">${escapeHtml(comparisonNote)}</div>` : ''}
      ${complianceFooter(DISCLAIMER.benchmark)}
    </div>
  `;
}

export function onStressSlider(): void {
  const getV = (id: string) => Number((document.getElementById(id) as HTMLInputElement | null)?.value || 0);
  const rate = getV('pa-rate');
  const vac = getV('pa-vac');
  const rent = getV('pa-rent');
  const val = getV('pa-val');
  const lbl = (id: string, v: string) => { const el = document.getElementById(id); if (el) el.textContent = v; };
  lbl('pa-rate-val', rate.toFixed(2));
  lbl('pa-vac-val', String(vac));
  lbl('pa-rent-val', String(rent));
  lbl('pa-val-val', String(val));
  if (stressDebounce) clearTimeout(stressDebounce);
  stressDebounce = setTimeout(runStressTest, 260);
}

async function runStressTest(): Promise<void> {
  const el = document.getElementById('pa-stress-result');
  if (!el) return;
  const getV = (id: string) => Number((document.getElementById(id) as HTMLInputElement | null)?.value || 0);
  try {
    const r = await api<AnyRecord>('POST', '/portfolio/stress-test', {
      rate_delta_pct: getV('pa-rate'),
      vacancy_delta_pct: getV('pa-vac'),
      rent_delta_pct: getV('pa-rent'),
      value_delta_pct: getV('pa-val'),
    });
    renderStressResult(el, r);
  } catch (err: any) {
    el.innerHTML = `<div style="color:var(--warning);font-size:12px;padding:8px 0">${escapeHtml(err.message || 'Stress test failed')}</div>`;
  }
}

// ═══════════════════════════════════════════════════════════════
// REHAB TAB (Phase 4A/4B/4C) — flip project tracker
// ═══════════════════════════════════════════════════════════════

async function renderRehabTab(el: HTMLElement, p: AnyRecord): Promise<void> {
  el.innerHTML = `<div style="color:var(--text-secondary);font-size:13px;padding:12px 0">Loading rehab + comps...</div>`;
  try {
    const [rehab, profit, comps] = await Promise.all([
      api<AnyRecord>('GET', `/portfolio/properties/${p.id}/rehab`),
      api<AnyRecord>('GET', `/portfolio/properties/${p.id}/profit`),
      api<AnyRecord>('GET', `/portfolio/properties/${p.id}/comps`),
    ]);

    el.innerHTML = `
      ${renderProfitThermometer(profit)}
      ${renderCompSection(comps)}
      ${renderRehabSection(rehab, String(p.id))}
    `;
    if (window.feather) window.feather.replace();
  } catch (err: any) {
    el.innerHTML = `<div class="portfolio-empty"><p>${escapeHtml(err.message || 'Failed to load')}</p></div>`;
  }
}

function renderProfitThermometer(pf: AnyRecord): string {
  // GET /portfolio/properties/{id}/profit = portfolio_metrics.
  // flip_profit_breakdown — the same model behind the flip card's
  // metrics.flip_profit, so `projected_profit` here equals the card.
  // total_in = purchase + closing + max(rehab budgeted, actual) + holding
  // cost accrued since purchase; the segments below use the same terms so
  // the bar sums to total_in + selling + profit.
  const purchase = Number(pf.purchase_price || 0);
  const closing = Number(pf.closing_costs || 0);
  const rehab = Math.max(Number(pf.rehab_budgeted || 0), Number(pf.rehab_actual || 0));
  const holding = Number(pf.holding_cost_accrued || 0);
  const holdingDays = Number(pf.holding_days || 0);
  const dailyHolding = Number(pf.daily_holding_cost || 0);
  const selling = Number(pf.selling_costs || 0);
  const arv = Number(pf.arv || 0);
  const profit = Number(pf.projected_profit || 0);
  const totalIn = Number(pf.total_in || 0);
  const roiPct = toNumber(pf.projected_roi_pct);
  const note = pf.note ? String(pf.note) : '';

  // Normalize each segment as % of ARV (or total_in if ARV not set).
  const denom = Math.max(arv, totalIn + Math.max(0, profit), 1);
  const segs = [
    { label: 'Purchase', value: purchase, color: '#6366f1' },
    { label: 'Closing', value: closing, color: '#818cf8' },
    { label: 'Rehab', value: rehab, color: '#f59e0b' },
    { label: 'Holding', value: holding, color: '#fb923c' },
    { label: 'Selling', value: selling, color: '#f472b6' },
    { label: 'Profit', value: Math.max(0, profit), color: profit >= 0 ? '#22c55e' : '#ef4444' },
  ];
  const profitTone = profit >= 0 ? 'pos' : 'neg';

  const bars = segs
    .filter((s) => s.value > 0)
    .map((s) => {
      const pct = (s.value / denom) * 100;
      return `<div class="flip-therm-seg" style="width:${pct.toFixed(2)}%;background:${s.color}" title="${escapeHtml(s.label)}: ${fmtMoney(s.value)}"></div>`;
    })
    .join('');

  return `
    <section class="portfolio-detail-section">
      <h4>Profit thermometer</h4>
      <div class="flip-therm-summary">
        <div><div class="flip-therm-lbl">Total in</div><div class="flip-therm-val">${totalIn > 0 ? fmtMoneyCompact(totalIn) : NA}</div></div>
        <div><div class="flip-therm-lbl">ARV</div><div class="flip-therm-val">${arv > 0 ? fmtMoneyCompact(arv) : NA}</div></div>
        <div><div class="flip-therm-lbl">Projected profit</div><div class="flip-therm-val ${arv > 0 ? profitTone : ''}">${arv > 0 ? fmtMoney(profit) : NA}</div></div>
        <div><div class="flip-therm-lbl">ROI</div><div class="flip-therm-val ${roiPct == null ? '' : profitTone}">${roiPct == null ? NA : `${roiPct.toFixed(1)}%`}</div></div>
      </div>
      ${note ? `<div class="flip-therm-note">${escapeHtml(note)}</div>` : ''}
      <div class="flip-therm-bar">${bars}</div>
      <div class="flip-therm-legend">
        ${segs.map((s) => `
          <div class="flip-therm-chip">
            <span class="flip-therm-dot" style="background:${s.color}"></span>
            ${escapeHtml(s.label)} <strong>${fmtMoneyCompact(s.value)}</strong>
          </div>
        `).join('')}
      </div>
      ${holding > 0 ? `<div class="flip-therm-note">Holding cost accrued: ${holdingDays}d \u00d7 ${fmtMoney(dailyHolding)}/day = ${fmtMoney(holding)}</div>` : ''}
    </section>
  `;
}

function renderCompSection(c: AnyRecord): string {
  const comps = (c.comps as AnyRecord[]) || [];
  const sug = c.suggestion as AnyRecord | null;
  if (comps.length === 0) {
    return `
      <section class="portfolio-detail-section">
        <h4>Comps near this property</h4>
        <div class="portfolio-pnl-empty">${escapeHtml(String(c.note || 'No recent comps found in this zip.'))}</div>
      </section>
    `;
  }
  const sugClass = sug?.severity === 'high' ? 'warn' : sug?.severity === 'low' ? 'info' : 'ok';
  return `
    <section class="portfolio-detail-section">
      <h4>Comps near this property</h4>
      ${sug ? `<div class="flip-comp-suggestion flip-comp-${sugClass}">${escapeHtml(String(sug.message))}</div>` : ''}
      <table class="portfolio-pnl-table">
        <tr><th>Address</th><th class="right">Price</th><th class="right">$/sqft</th></tr>
        ${comps.map((comp) => `
          <tr>
            <td>${escapeHtml(String(comp.address || ''))}</td>
            <td class="right"><strong>${fmtMoneyCompact(Number(comp.price || 0))}</strong></td>
            <td class="right">${comp.price_per_sqft ? '$' + comp.price_per_sqft : '—'}</td>
          </tr>
        `).join('')}
      </table>
    </section>
  `;
}

function renderRehabSection(r: AnyRecord, propertyId: string): string {
  const items = (r.items as AnyRecord[]) || [];
  const totalBudget = Number(r.total_budgeted || 0);
  const totalActual = Number(r.total_actual || 0);
  const variance = Number(r.variance || 0);
  const variancePct = totalBudget > 0 ? (variance / totalBudget) * 100 : 0;
  const tone = variancePct > 10 ? 'neg' : variancePct > 0 ? 'warn' : 'pos';

  const byCat = (r.by_category || {}) as Record<string, { budgeted: number; actual: number }>;
  const catRows = Object.entries(byCat)
    .map(([cat, v]) => {
      const pct = v.budgeted > 0 ? Math.min(200, (v.actual / v.budgeted) * 100) : 0;
      const catTone = pct > 110 ? 'neg' : pct > 100 ? 'warn' : 'pos';
      return `
        <tr>
          <td>${escapeHtml(cat.replace(/_/g, ' '))}</td>
          <td class="right">${fmtMoney(v.budgeted)}</td>
          <td class="right"><strong class="${catTone === 'pos' ? 'pos' : catTone === 'warn' ? '' : 'neg'}">${fmtMoney(v.actual)}</strong></td>
          <td class="right">${pct.toFixed(0)}%</td>
        </tr>
      `;
    }).join('');

  const itemRows = items.map((it) => {
    const variance = Number(it.variance || 0);
    const vCls = variance > 0 ? 'neg' : 'pos';
    return `
      <div class="rehab-item">
        <div class="rehab-item-head">
          <div>
            <div class="rehab-item-cat">${escapeHtml(String(it.category).replace(/_/g, ' '))}</div>
            <div class="rehab-item-desc">${escapeHtml(String(it.description || ''))}</div>
          </div>
          <button class="portfolio-tx-delete" onclick="deleteRehabItem('${escapeHtml(String(it.id))}')" aria-label="Delete">
            <i data-feather="x" width="12"></i>
          </button>
        </div>
        <div class="rehab-item-meta">
          <span>Budgeted <strong>${fmtMoney(Number(it.budgeted_amount))}</strong></span>
          <span>Actual <strong>${fmtMoney(Number(it.actual_amount))}</strong></span>
          <span class="${vCls}">Δ <strong>${fmtMoney(variance)}</strong></span>
          <span class="rehab-status rehab-status-${it.status}">${escapeHtml(String(it.status).replace(/_/g, ' '))}</span>
        </div>
        ${it.contractor ? `<div class="rehab-item-contractor">Contractor: ${escapeHtml(String(it.contractor))}${it.contractor_phone ? ' · ' + escapeHtml(String(it.contractor_phone)) : ''}</div>` : ''}
      </div>
    `;
  }).join('');

  return `
    <section class="portfolio-detail-section">
      <h4>Rehab project</h4>
      <div class="portfolio-pnl-summary">
        <div><div class="portfolio-pnl-summary-label">Budgeted</div><div class="portfolio-pnl-summary-value">${fmtMoney(totalBudget)}</div></div>
        <div><div class="portfolio-pnl-summary-label">Actual</div><div class="portfolio-pnl-summary-value">${fmtMoney(totalActual)}</div></div>
        <div><div class="portfolio-pnl-summary-label">Variance</div><div class="portfolio-pnl-summary-value ${tone === 'pos' ? 'pos' : tone === 'neg' ? 'neg' : ''}">${fmtMoney(variance)}</div></div>
      </div>
      <button class="btn btn-secondary btn-sm" onclick="openRehabModal('${escapeHtml(propertyId)}')" style="margin-top:10px">
        <i data-feather="plus" width="13"></i><span>Add rehab item</span>
      </button>
      ${catRows ? `
        <div style="margin-top:12px;font-size:11px;font-weight:700;color:var(--primary);text-transform:uppercase;letter-spacing:0.05em;margin-bottom:6px">By category</div>
        <table class="portfolio-pnl-table">
          <tr><th>Category</th><th class="right">Budgeted</th><th class="right">Actual</th><th class="right">%</th></tr>
          ${catRows}
        </table>
      ` : ''}
      ${items.length ? `
        <div style="margin-top:14px;font-size:11px;font-weight:700;color:var(--primary);text-transform:uppercase;letter-spacing:0.05em;margin-bottom:8px">Line items (${items.length})</div>
        <div class="rehab-list">${itemRows}</div>
      ` : '<div class="portfolio-pnl-empty" style="margin-top:10px">No rehab items yet — add one above to track budget vs actual.</div>'}
    </section>
  `;
}

export function openRehabModal(propertyId: string): void {
  const openModal = (window as AnyRecord as {
    openModal: (t: string, body: string, buttons: AnyRecord[]) => void;
  }).openModal;
  const closeModal = (window as AnyRecord as { closeModal: () => void }).closeModal;
  const body = `
    <div class="portfolio-form">
      <label>Category
        <select id="rh-cat">
          <option value="kitchen">Kitchen</option>
          <option value="bathroom">Bathroom</option>
          <option value="flooring">Flooring</option>
          <option value="electrical">Electrical</option>
          <option value="plumbing">Plumbing</option>
          <option value="roofing">Roofing</option>
          <option value="exterior">Exterior</option>
          <option value="landscaping">Landscaping</option>
          <option value="permits">Permits</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label>Description<input type="text" id="rh-desc" placeholder="e.g. Cabinets + countertops" required></label>
      <div class="portfolio-form-row">
        <label>Budgeted<input type="number" id="rh-bud" min="0" placeholder="$"></label>
        <label>Actual<input type="number" id="rh-act" min="0" placeholder="$"></label>
      </div>
      <div class="portfolio-form-row">
        <label>Status
          <select id="rh-status">
            <option value="pending">Pending</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
            <option value="over_budget">Over budget</option>
          </select>
        </label>
        <label>&nbsp;<div style="height:34px"></div></label>
      </div>
      <div class="portfolio-form-row">
        <label>Contractor<input type="text" id="rh-contractor" placeholder="Name"></label>
        <label>Phone<input type="text" id="rh-phone" placeholder="555-555-5555"></label>
      </div>
      <label>Notes<textarea id="rh-notes" rows="2"></textarea></label>
    </div>
  `;
  openModal('Add rehab item', body, [
    { label: 'Cancel', style: 'secondary', onclick: closeModal },
    { label: 'Add', style: 'primary', onclick: () => submitRehab(propertyId) },
  ]);
}

async function submitRehab(propertyId: string): Promise<void> {
  const getV = (id: string) => (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value?.trim() || '';
  const getN = (id: string) => {
    const v = getV(id);
    return v ? Number(v) : null;
  };
  const desc = getV('rh-desc');
  const bud = getN('rh-bud');
  if (!desc || bud == null) {
    showToast('Description and budget are required', 'error');
    return;
  }
  const payload: AnyRecord = {
    category: getV('rh-cat'),
    description: desc,
    budgeted_amount: bud,
    actual_amount: getN('rh-act'),
    status: getV('rh-status') || 'pending',
    contractor: getV('rh-contractor') || null,
    contractor_phone: getV('rh-phone') || null,
    notes: getV('rh-notes') || null,
  };
  Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);
  try {
    await api('POST', `/portfolio/properties/${propertyId}/rehab`, payload);
    (window as AnyRecord).closeModal?.();
    showToast('Added', 'success');
    renderDetailTab();
  } catch (err: any) {
    showToast(err.message || 'Could not add', 'error');
  }
}

export async function deleteRehabItem(itemId: string): Promise<void> {
  if (!confirm('Remove this rehab item?')) return;
  try {
    await api('DELETE', `/portfolio/rehab/${itemId}`);
    showToast('Removed', 'success');
    renderDetailTab();
  } catch (err: any) {
    showToast(err.message || 'Could not remove', 'error');
  }
}

function renderStressResult(el: HTMLElement, r: AnyRecord): void {
  const p = (r.portfolio || {}) as AnyRecord;
  const atRisk = (r.properties as AnyRecord[] || []).filter((x) => (x.risk_flags || []).length > 0);

  el.innerHTML = `
    <div class="portfolio-stress-grid">
      <div class="portfolio-stress-cell">
        <div class="portfolio-stress-label">Cash flow</div>
        <div class="portfolio-stress-before">${fmtMoney(Number(p.baseline_monthly_cash_flow || 0))}</div>
        <div class="portfolio-stress-after ${Number(p.shocked_monthly_cash_flow) >= 0 ? 'pos' : 'neg'}">
          → ${fmtMoney(Number(p.shocked_monthly_cash_flow || 0))}/mo
        </div>
      </div>
      <div class="portfolio-stress-cell">
        <div class="portfolio-stress-label">Equity</div>
        <div class="portfolio-stress-before">${fmtMoneyCompact(Number(p.baseline_equity || 0))}</div>
        <div class="portfolio-stress-after ${Number(p.equity_delta) >= 0 ? 'pos' : 'neg'}">
          → ${fmtMoneyCompact(Number(p.shocked_equity || 0))}
        </div>
      </div>
      <div class="portfolio-stress-cell">
        <div class="portfolio-stress-label">LTV</div>
        <div class="portfolio-stress-before">${Number(p.baseline_ltv || 0).toFixed(1)}%</div>
        <div class="portfolio-stress-after ${Number(p.shocked_ltv) <= 80 ? 'pos' : 'neg'}">
          → ${Number(p.shocked_ltv || 0).toFixed(1)}%
        </div>
      </div>
    </div>
    ${atRisk.length ? `
    <div class="portfolio-stress-atrisk">
      <div class="portfolio-stress-atrisk-head">${atRisk.length} propert${atRisk.length === 1 ? 'y' : 'ies'} at risk</div>
      ${atRisk.map((a) => `
        <div class="portfolio-stress-atrisk-row">
          <strong>${escapeHtml(String(a.address))}</strong>
          <span>${(a.risk_flags as string[]).map((f) => f.replace(/_/g, ' ')).join(' · ')}</span>
        </div>
      `).join('')}
    </div>
    ` : '<div class="portfolio-stress-ok">All properties remain above safe thresholds under this scenario.</div>'}
  `;
}

